import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Grid, Card, CardMedia, Box, Typography, Button, TextField, Avatar, Menu, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, CircularProgress, LinearProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
// Tesseract.js nie jest już potrzebny - używamy backendu
// import Tesseract from 'tesseract.js';
import styles from './Pedigree.module.css';
import Logo from '../../assets/logo_white.png';
import AvatarImage from '../../assets/PigeonsCatalog/Avatar.png';
import pigeonImage from '../../assets/main-pigeon.png';
import customIcon1 from '../../assets/PigeonsCatalog/pearson_catalog.svg';
import customIcon2 from '../../assets/PigeonsCatalog/wing_catalog.svg';
import customIcon3 from '../../assets/PigeonsCatalog/pair_catalog.svg';
import customIcon4 from '../../assets/PigeonsCatalog/genealogy_catalog.svg';
import customIcon5 from '../../assets/PigeonsCatalog/flight_catalog.svg';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const Pedigree = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [columns, setColumns] = useState(3); // Domyślnie 3 kolumny
    const [availableHeight, setAvailableHeight] = useState(600);
    const [selectedPigeonId, setSelectedPigeonId] = useState(null);
    const [pigeonsList, setPigeonsList] = useState([]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPigeon, setEditingPigeon] = useState(null);
    const [editingPath, setEditingPath] = useState(null); // Ścieżka do edytowanego gołębia w drzewie (np. ['father', 'mother'])
    const [pedigreeData, setPedigreeData] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '',
        hodowla: '',
        id: '',
        color: '',
        opis: ''
    });
    const [ocrEditDialogOpen, setOcrEditDialogOpen] = useState(false);
    const [ocrParsedData, setOcrParsedData] = useState(null);
    const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
    const [ocrImage, setOcrImage] = useState(null);
    const [ocrProgress, setOcrProgress] = useState(0);
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrResult, setOcrResult] = useState('');
    const fileInputRef = useRef(null);
    const pedigreeExportRef = useRef(null);
    const [breederData, setBreederData] = useState({ name: '', logo: null });
    const [profileData, setProfileData] = useState({ firstName: '', lastName: '', city: '', phone: '', email: '' });
    const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
    const fontSizesByGeneration = { 0: 14, 1: 13, 2: 11, 3: 10, 4: 9 };
    const charLimitsByGeneration = { 0: 120, 1: 80, 2: 50, 3: 30, 4: 20 };
    const gridRowHeightByGenerations = { 2: 180, 3: 140, 4: 100, 5: 88 };
    const minGridWidthByGenerations = { 3: 600, 4: 900, 5: 1200 };

    /** Wysokość obszaru siatki (px) w podglądzie wydruku / PDF — dla 5 gen. nie ściska wierszy poniżej czytelnego minimum. */
    const getPedigreeGridContentHeightPx = (depth) => {
        const gridRows = Math.pow(2, depth);
        const gap = depth === 3 ? 8 : 6;
        const minRowHeight = depth <= 3 ? 26 : depth === 4 ? 22 : 20;
        const needed = gridRows * minRowHeight + (gridRows - 1) * gap;
        const legacyFloor = depth <= 3 ? 850 : depth === 4 ? 800 : 750;
        return Math.max(legacyFloor, needed);
    };

    // Wczytaj dane hodowli z localStorage
    useEffect(() => {
        const saved = localStorage.getItem('breederData');
        if (saved) {
            try {
                setBreederData(JSON.parse(saved));
            } catch (error) {
                console.error('Błąd wczytywania danych hodowli:', error);
            }
        }
        
        // Wczytaj dane profilu (dla nagłówka)
        const profileSaved = localStorage.getItem('profileData');
        if (profileSaved) {
            try {
                setProfileData(JSON.parse(profileSaved));
            } catch (error) {
                console.error('Błąd wczytywania danych profilu:', error);
            }
        } else {
            // Domyślne dane profilu
            setProfileData({
                firstName: 'Krzysztof',
                lastName: 'Nowak',
                city: 'Łódź',
                phone: '600 500 400',
                email: 'k.nowak@gmail.com'
            });
        }
    }, []);
    
    // Funkcja do wczytywania gołębi z localStorage
    const loadPigeonsList = () => {
        const saved = localStorage.getItem('pigeonsData');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.map(p => ({
                    ...p,
                    dateAdded: new Date(p.dateAdded)
                }));
            } catch (e) {
                console.error('Błąd wczytywania danych z localStorage:', e);
            }
        }
        return [];
    };

    // Funkcja do wczytywania rodowodu dla gołębia z localStorage
    const loadPedigreeForPigeon = (pigeonId) => {
        const saved = localStorage.getItem(`pedigree_${pigeonId}`);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Błąd wczytywania rodowodu:', e);
            }
        }
        return null;
    };

    // Funkcja do zapisywania rodowodu dla gołębia do localStorage
    const savePedigreeForPigeon = (pigeonId, pedigree) => {
        localStorage.setItem(`pedigree_${pigeonId}`, JSON.stringify(pedigree));
    };

    // Funkcja do tworzenia domyślnego rodowodu z danych gołębia
    const createDefaultPedigree = (pigeon) => {
        return {
            hodowla: pigeon.name || '',
            image: pigeon.image || pigeonImage,
            id: pigeon.number || '',
            gender: pigeon.gender === 'Samiec' ? 'M' : 'O',
            color: '',
            status: pigeon.status || '',
            opis: '',
            dodatkoweInformacje: '',
            father: null,
            mother: null
        };
    };

    useEffect(() => {
        const updateHeight = () => {
            setAvailableHeight(window.innerHeight - 250);
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    // Wczytaj listę gołębi przy montowaniu komponentu i aktualizuj przy zmianach
    useEffect(() => {
        const loadPigeons = () => {
            const pigeons = loadPigeonsList();
            setPigeonsList(pigeons);
            
            // Sprawdź czy jest parametr pigeonId w URL
            const pigeonIdFromUrl = searchParams.get('pigeonId');
            if (pigeonIdFromUrl) {
                const pigeonId = parseInt(pigeonIdFromUrl, 10);
                if (pigeons.find(p => p.ID === pigeonId)) {
                    setSelectedPigeonId(pigeonId);
                    // Usuń parametr z URL po załadowaniu
                    navigate('/pedigree', { replace: true });
                    return;
                }
            }
            
            if (pigeons.length > 0 && !selectedPigeonId) {
                // Wybierz pierwszego gołębia domyślnie
                setSelectedPigeonId(pigeons[0].ID);
            }
        };
        
        loadPigeons();
        
        // Nasłuchuj zmian w localStorage (gdy użytkownik dodaje/usuwa gołębie w zakładce "Gołębie")
        const handleStorageChange = () => {
            loadPigeons();
        };
        
        window.addEventListener('storage', handleStorageChange);
        
        // Sprawdzaj zmiany co sekundę (dla tego samego okna przeglądarki)
        const interval = setInterval(loadPigeons, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, [selectedPigeonId, searchParams, navigate]);

    // Wczytaj rodowód gdy wybrano gołębia
    useEffect(() => {
        if (selectedPigeonId) {
            const pigeon = pigeonsList.find(p => p.ID === selectedPigeonId);
            if (pigeon) {
                let pedigree = loadPedigreeForPigeon(selectedPigeonId);
                if (!pedigree) {
                    // Utwórz domyślny rodowód
                    pedigree = createDefaultPedigree(pigeon);
                    savePedigreeForPigeon(selectedPigeonId, pedigree);
                }
                setPedigreeData(pedigree);
            }
        }
    }, [selectedPigeonId, pigeonsList]);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        setAnchorEl(null);
        navigate('/');
    };

    const handleClick = (index, name) => {
        return () => {
            switch(index) {
                case 0: // Panel hodowcy
                    navigate('/panel');
                    break;
                case 1: // Gołębie
                    navigate('/pigeons-new');
                    break;
                case 2: // Rodowody
                    // Już jesteśmy tutaj
                    break;
                case 3: // Parowanie
                    navigate('/pairing');
                    break;
                case 4: // Loty
                    navigate('/flights');
                    break;
                default:
                    break;
            }
        };
    }

    const iconLabels = [
        'Panel hodowcy',
        'Gołębie',
        'Rodowody',
        'Parowanie',
        'Loty'
    ];

    const icons = [customIcon1, customIcon2, customIcon4, customIcon3, customIcon5];

    // Funkcja do zbierania wszystkich gołębi z danego poziomu
    const getPigeonsAtLevel = (pigeon, targetLevel, currentLevel = 0) => {
        if (!pigeon) return [];
        if (currentLevel === targetLevel) {
            return [pigeon];
        }
        
        const result = [];
        if (pigeon.father) {
            result.push(...getPigeonsAtLevel(pigeon.father, targetLevel, currentLevel + 1));
        }
        if (pigeon.mother) {
            result.push(...getPigeonsAtLevel(pigeon.mother, targetLevel, currentLevel + 1));
        }
        
        return result;
    };


    // Funkcja do znajdowania gołębia w drzewie po ścieżce
    const getPigeonByPath = (pedigree, path) => {
        if (!path || path.length === 0) return pedigree;
        let current = pedigree;
        for (const step of path) {
            if (!current || !current[step]) return null;
            current = current[step];
        }
        return current;
    };

    // Funkcja do głębokiego klonowania obiektu
    const deepClone = (obj) => {
        if (obj === null || typeof obj !== 'object') return obj;
        if (obj instanceof Date) return new Date(obj.getTime());
        if (Array.isArray(obj)) return obj.map(item => deepClone(item));
        
        const cloned = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                cloned[key] = deepClone(obj[key]);
            }
        }
        return cloned;
    };

    const truncateByGeneration = (text, generation) => {
        const max = charLimitsByGeneration[generation] ?? 20;
        if (!text) return '';
        return text.length > max ? `${text.slice(0, max)}…` : text;
    };

    // Funkcja do aktualizacji gołębia w drzewie po ścieżce
    const updatePigeonByPath = (pedigree, path, updates) => {
        // Tworzymy głęboką kopię całego drzewa, żeby nie modyfikować oryginału
        const newPedigree = deepClone(pedigree);
        
        if (!path || path.length === 0) {
            // Aktualizacja głównego gołębia - zachowujemy wszystkie właściwości (father, mother, itp.)
            return { ...newPedigree, ...updates };
        }
        
        let current = newPedigree;
        
        // Przechodzimy przez ścieżkę, tworząc kopie na każdym poziomie
        for (let i = 0; i < path.length - 1; i++) {
            const step = path[i];
            if (!current[step]) {
                current[step] = {};
            }
            // Tworzymy kopię obiektu, zachowując wszystkie właściwości (father, mother, itp.)
            current[step] = { ...current[step] };
            current = current[step];
        }
        
        const lastStep = path[path.length - 1];
        if (!current[lastStep]) {
            current[lastStep] = {};
        }
        // Aktualizujemy tylko podane pola, zachowując resztę (father, mother, itp.)
        current[lastStep] = { ...current[lastStep], ...updates };
        
        return newPedigree;
    };

    // Funkcja do otwierania dialogu edycji
    const handleEditPigeon = (pigeon, path) => {
        if (!pigeon) return;
        setEditingPigeon(pigeon);
        setEditingPath(path);
        setEditForm({
            name: pigeon.name || '',
            hodowla: pigeon.hodowla || '',
            id: pigeon.id || '',
            color: pigeon.color || '',
            opis: pigeon.opis || ''
        });
        setEditDialogOpen(true);
    };

    // Funkcja do zapisywania zmian w rodowodzie
    const handleSaveEdit = () => {
        if (!pedigreeData) {
            alert('Błąd: Brak danych rodowodu');
            return;
        }
        
        const formData = {
            id: editForm.id || '',
            name: editForm.name || '',
            hodowla: editForm.hodowla || '',
            color: editForm.color || '',
            opis: editForm.opis || ''
        };
        if (formData.name && !formData.hodowla) {
            formData.hodowla = formData.name;
        }
        
        console.log('Zapisywanie danych:', { editingPath, formData, pedigreeData });
        
        // editingPath może być null dla głównego gołębia
        const updatedPedigree = updatePigeonByPath(pedigreeData, editingPath, formData);
        console.log('Zaktualizowany rodowód:', updatedPedigree);
        
        setPedigreeData(updatedPedigree);
        
        if (selectedPigeonId) {
            savePedigreeForPigeon(selectedPigeonId, updatedPedigree);
        }
        
        setEditDialogOpen(false);
        setEditingPigeon(null);
        setEditingPath(null);
        
        alert('Zmiany zostały zapisane!');
    };

    const handleCloseEditDialog = () => {
        setEditDialogOpen(false);
        setEditingPigeon(null);
        setEditingPath(null);
    };

    // Funkcja do wczytywania zdjęcia
    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setOcrImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Funkcja do parsowania wyników OCR i wyciągania danych
    const parseOCRResult = (text) => {
        const result = {
            main: {
                hodowla: '',
                id: '',
                gender: '',
                color: '',
                status: '',
                opis: '',
                dodatkoweInformacje: ''
            },
            father: {
                hodowla: '',
                id: '',
                gender: 'M',
                color: '',
                status: '',
                opis: '',
                dodatkoweInformacje: ''
            },
            mother: {
                hodowla: '',
                id: '',
                gender: 'O',
                color: '',
                status: '',
                opis: '',
                dodatkoweInformacje: ''
            }
        };

        // Szukanie numerów obrączek - precyzyjne wzorce dla numerów obrączek gołębi
        // Format: PL-XXXX-XXXXX-X (gdzie XXXX to rok, potem 4-5 cyfr, potem 1 cyfra)
        // Priorytetyzujemy numery zaczynające się od PL
        
        // Najpierw szukamy numerów z PL (najbardziej prawdopodobne)
        const plRingPatterns = [
            /PL[-\s]?\d{4}[-\s]?\d{4,5}[-\s]?\d{1}[a-z]?/gi,  // PL-XXXX-XXXXX-X (z ostatnią cyfrą)
            /PL[-\s]?\d{4}[-\s]?\d{4,5}/gi,                   // PL-XXXX-XXXXX (bez ostatniej cyfry)
        ];
        
        // Potem numery bez PL, ale z odpowiednią strukturą (rok + 4-5 cyfr + opcjonalna cyfra)
        const noPlRingPatterns = [
            /\b\d{4}[-\s]?\d{4,5}[-\s]?\d{1}[a-z]?\b/gi,      // XXXX-XXXXX-X (z ostatnią cyfrą)
            /\b\d{4}[-\s]?\d{4,5}\b/gi,                       // XXXX-XXXXX (bez ostatniej cyfry)
        ];
        
        let ringNumbers = [];
        
        // Najpierw zbierz numery z PL
        for (const pattern of plRingPatterns) {
            const matches = text.match(pattern);
            if (matches) {
                ringNumbers = [...ringNumbers, ...matches];
            }
        }
        
        // Potem numery bez PL, ale tylko jeśli nie wyglądają jak numery telefonów
        for (const pattern of noPlRingPatterns) {
            const matches = text.match(pattern);
            if (matches) {
                // Filtruj numery telefonów (9 cyfr bez separatorów lub z typowymi separatorami telefonów)
                const filtered = matches.filter(match => {
                    const digitsOnly = match.replace(/\D/g, '');
                    // Odrzuć jeśli ma dokładnie 9 cyfr (numer telefonu w Polsce)
                    if (digitsOnly.length === 9) return false;
                    // Odrzuć jeśli ma format telefonu (XXX-XXX-XXX lub XXX XXX XXX z 9 cyframi)
                    if (/^\d{3}[-\s]?\d{3}[-\s]?\d{3}$/.test(match)) return false;
                    // Odrzuć jeśli zaczyna się od +48, 48, lub ma format międzynarodowy
                    if (/^(\+?48|0048)[-\s]?\d/.test(match)) return false;
                    // Zaakceptuj tylko jeśli ma strukturę numeru obrączki (4 cyfry + 4-5 cyfr + opcjonalna cyfra)
                    return /^\d{4}[-\s]?\d{4,5}[-\s]?\d?/.test(match);
                });
                ringNumbers = [...ringNumbers, ...filtered];
            }
        }
        
        // Usuń duplikaty i posortuj (najpierw numery z PL, potem najdłuższe)
        ringNumbers = [...new Set(ringNumbers)].sort((a, b) => {
            const aHasPL = /^PL/i.test(a);
            const bHasPL = /^PL/i.test(b);
            if (aHasPL && !bHasPL) return -1;
            if (!aHasPL && bHasPL) return 1;
            return b.length - a.length;
        });
        
        // Szukanie nazw (słowa z wielką literą, mogą zawierać apostrofy)
        const namePattern = /[A-Z][a-zA-Z']+(?:\s+[A-Z][a-zA-Z']+)*/g;
        const names = text.match(namePattern) || [];
        
        // Szukanie płci
        const genderPattern = /\b(M|O|Samiec|Samica|M|F)\b/gi;
        const genders = text.match(genderPattern) || [];
        
        // Szukanie barw (częste kolory gołębi)
        const colorPattern = /\b(Niebieski|Czarny|Czerwony|Biały|Szary|Srebrny|Żółty|Brązowy|Czerwono-biały|Niebiesko-biały)\b/gi;
        const colors = text.match(colorPattern) || [];
        
        // Szukanie statusu
        const statusPattern = /\b(Rozpłodowy|Lotowy|Nieaktywny|Rozpłod|Lot|Nieaktywn)\b/gi;
        const statuses = text.match(statusPattern) || [];

        // Próba przypisania danych do głównego gołębia
        if (ringNumbers.length > 0) {
            result.main.id = ringNumbers[0].replace(/\s/g, '');
        }
        if (names.length > 0) {
            result.main.hodowla = names[0];
        }
        if (genders.length > 0) {
            const gender = genders[0].toUpperCase();
            result.main.gender = gender === 'M' || gender === 'SAMIEC' ? 'M' : 'O';
        }
        if (colors.length > 0) {
            result.main.color = colors[0];
        }
        if (statuses.length > 0) {
            result.main.status = statuses[0];
        }

        // Próba przypisania danych do ojca
        if (ringNumbers.length > 1) {
            result.father.id = ringNumbers[1].replace(/\s/g, '');
        }
        if (names.length > 1) {
            result.father.hodowla = names[1];
        }
        if (colors.length > 1) {
            result.father.color = colors[1];
        }
        if (statuses.length > 1) {
            result.father.status = statuses[1];
        }

        // Próba przypisania danych do matki
        if (ringNumbers.length > 2) {
            result.mother.id = ringNumbers[2].replace(/\s/g, '');
        }
        if (names.length > 2) {
            result.mother.hodowla = names[2];
        }
        if (colors.length > 2) {
            result.mother.color = colors[2];
        }
        if (statuses.length > 2) {
            result.mother.status = statuses[2];
        }

        return result;
    };

    // Funkcja do przetwarzania OCR przez backend z fallbackiem
    const handleOCRProcess = async () => {
        if (!ocrImage || !selectedPigeonId) {
            alert('Wybierz gołębia i wczytaj zdjęcie rodowodu');
            return;
        }

        setOcrLoading(true);
        setOcrProgress(0);
        setOcrResult('');

        try {
            // Konwertuj obraz na blob/file do wysłania
            const formData = new FormData();
            
            // Jeśli ocrImage to URL (z FileReader), musimy pobrać blob
            let imageBlob;
            if (ocrImage instanceof File) {
                imageBlob = ocrImage;
            } else if (typeof ocrImage === 'string') {
                // Jeśli to data URL, konwertuj na blob
                const response = await fetch(ocrImage);
                imageBlob = await response.blob();
            } else {
                throw new Error('Nieobsługiwany format obrazu');
            }
            
            formData.append('image', imageBlob, 'pedigree.jpg');

            // Wysyłanie do backendu
            const API_URL = process.env.REACT_APP_OCR_API_URL || 'http://localhost:3001';
            
            // Symulacja postępu (backend nie zwraca postępu w czasie rzeczywistym)
            const progressInterval = setInterval(() => {
                setOcrProgress(prev => {
                    if (prev >= 90) return prev;
                    return prev + 5;
                });
            }, 500);

            let response;
            try {
                // Sprawdź czy backend jest dostępny przed wysłaniem dużego pliku
                const healthCheck = await fetch(`${API_URL}/api/health`, {
                    method: 'GET',
                    signal: AbortSignal.timeout(3000) // Timeout 3 sekundy
                });
                
                if (!healthCheck.ok) {
                    throw new Error('Backend nie odpowiada');
                }
                
                response = await fetch(`${API_URL}/api/ocr/pedigree`, {
                    method: 'POST',
                    body: formData,
                    signal: AbortSignal.timeout(60000) // Timeout 60 sekund dla OCR
                });
            } catch (fetchError) {
                clearInterval(progressInterval);
                // Jeśli backend nie jest dostępny, użyj lokalnego Tesseract.js jako fallback
                console.warn('Backend nie jest dostępny, używam lokalnego OCR...', fetchError);
                if (fetchError.name === 'AbortError') {
                    console.warn('Timeout - backend nie odpowiada w rozsądnym czasie');
                }
                return handleOCRLocal();
            }

            clearInterval(progressInterval);
            setOcrProgress(100);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Błąd serwera' }));
                throw new Error(errorData.error || `Błąd HTTP: ${response.status}`);
            }

            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.error || 'Błąd przetwarzania OCR');
            }

            // Debug: sprawdź czy tekst został wykryty
            console.log('OCR Text:', data.text);
            console.log('OCR Text Length:', data.text?.length || 0);
            console.log('Parsed Data:', data.parsedData);

            if (!data.text || data.text.trim().length === 0) {
                alert('OCR nie wykrył żadnego tekstu. Sprawdź jakość obrazu i spróbuj ponownie.');
                setOcrLoading(false);
                setOcrProgress(0);
                return;
            }

            setOcrResult(data.text);
            setOcrParsedData(data.parsedData);
            
            // Zamknij dialog OCR i otwórz dialog edycji z wykrytymi danymi
            setOcrDialogOpen(false);
            setOcrEditDialogOpen(true);
        } catch (error) {
            console.error('Błąd OCR:', error);
            // Jeśli błąd nie jest związany z połączeniem, pokaż alert
            if (!error.message.includes('Failed to fetch') && !error.message.includes('NetworkError')) {
                alert('Wystąpił błąd podczas przetwarzania OCR: ' + error.message);
            }
        } finally {
            setOcrLoading(false);
            setOcrProgress(0);
        }
    };

    // Fallback: lokalne przetwarzanie OCR z Tesseract.js
    const handleOCRLocal = async () => {
        try {
            // Dynamiczny import Tesseract.js tylko gdy jest potrzebny
            const Tesseract = (await import('tesseract.js')).default;
            
            setOcrProgress(0);
            
            // Użyj Tesseract.js z zoptymalizowanymi ustawieniami
            const { data: { text: tesseractText } } = await Tesseract.recognize(ocrImage, 'pol+eng', {
                logger: m => {
                    if (m.status === 'recognizing text') {
                        setOcrProgress(Math.round(m.progress * 100));
                    }
                },
                // Wypróbuj różne tryby segmentacji strony
                tessedit_pageseg_mode: '6', // Uniform block of text (lepsze dla dokumentów)
                tessedit_ocr_engine_mode: '1', // LSTM engine
                // Zwiększ czułość wykrywania
                tessedit_char_whitelist: '', // Usuń ograniczenia
            });
            
            const text = tesseractText;
            
            // Debug: sprawdź czy tekst został wykryty
            console.log('OCR Text (local):', text);
            console.log('OCR Text Length:', text?.length || 0);

            if (!text || text.trim().length === 0) {
                alert('OCR nie wykrył żadnego tekstu. Sprawdź jakość obrazu i spróbuj ponownie.');
                setOcrLoading(false);
                setOcrProgress(0);
                return;
            }

            setOcrResult(text);
            
            // Parsuj wyniki OCR
            const parsedData = parseOCRResult(text);
            console.log('Parsed Data:', parsedData);
            setOcrParsedData(parsedData);
            
            // Zamknij dialog OCR i otwórz dialog edycji z wykrytymi danymi
            setOcrDialogOpen(false);
            setOcrEditDialogOpen(true);
        } catch (error) {
            console.error('Błąd lokalnego OCR:', error);
            alert('Wystąpił błąd podczas przetwarzania OCR. Upewnij się, że backend jest uruchomiony lub sprawdź konsolę przeglądarki.');
        } finally {
            setOcrLoading(false);
            setOcrProgress(0);
        }
    };

    const handleCloseOcrDialog = () => {
        setOcrDialogOpen(false);
        setOcrImage(null);
        setOcrResult('');
        setOcrProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Funkcja do otwarcia podglądu eksportu
    const handleOpenExportPreview = () => {
        if (!pedigreeData) {
            alert('Brak danych rodowodu do eksportu');
            return;
        }
        setExportPreviewOpen(true);
    };

    // Funkcja do eksportu rodowodu do PDF
    const handleExportPedigree = async () => {
        if (!pedigreeData || !pedigreeExportRef.current) {
            alert('Brak danych rodowodu do eksportu');
            return;
        }
        
        // Zamknij dialog podglądu
        setExportPreviewOpen(false);

        try {
            // Pokaż loading
            const loadingElement = document.createElement('div');
            loadingElement.style.cssText = `
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: white;
                padding: 20px 40px;
                border-radius: 10px;
                box-shadow: 0 4px 20px rgba(0,0,0,0.3);
                z-index: 10000;
                font-family: 'Poppins', sans-serif;
            `;
            loadingElement.textContent = 'Generowanie PDF...';
            document.body.appendChild(loadingElement);

            // Zapisz oryginalne style elementu
            const element = pedigreeExportRef.current;
            const originalOverflow = element.style.overflow;
            const originalMaxWidth = element.style.maxWidth;
            const originalMaxHeight = element.style.maxHeight;
            
            // Ustaw style dla eksportu - upewnij się, że wszystko jest widoczne
            element.style.overflow = 'visible';
            element.style.maxWidth = 'none';
            element.style.maxHeight = 'none';

            // Czekaj chwilę, żeby UI się zaktualizował
            await new Promise(resolve => setTimeout(resolve, 300));

            // Pobierz rzeczywiste wymiary elementu
            const rect = element.getBoundingClientRect();
            const scrollWidth = element.scrollWidth || rect.width;
            const scrollHeight = element.scrollHeight || rect.height;

            // Konwertuj rodowód na obraz z lepszymi ustawieniami
            const canvas = await html2canvas(element, {
                scale: 2,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true,
                width: scrollWidth,
                height: scrollHeight,
                x: 0,
                y: 0,
                scrollX: 0,
                scrollY: 0,
                windowWidth: scrollWidth,
                windowHeight: scrollHeight,
            });

            // Przywróć oryginalne style
            element.style.overflow = originalOverflow;
            element.style.maxWidth = originalMaxWidth;
            element.style.maxHeight = originalMaxHeight;

            // Usuń loading
            document.body.removeChild(loadingElement);

            // Utwórz PDF w orientacji portrait (A4 pionowy)
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            
            // Konwertuj wymiary canvas z px na mm (przy skali 2)
            // html2canvas zwraca wymiary w px, ale przy scale: 2, więc rzeczywiste wymiary są 2x większe
            const imgWidth = canvas.width / 2; // Rzeczywista szerokość w px
            const imgHeight = canvas.height / 2; // Rzeczywista wysokość w px
            
            // Konwertuj px na mm (1px = 0.264583mm przy 96 DPI)
            const imgWidthMM = imgWidth * 0.264583;
            const imgHeightMM = imgHeight * 0.264583;
            
            // Oblicz skalę, aby zmieścić obraz na stronie z marginesami
            const margin = 5; // 5mm margines z każdej strony
            const availableWidth = pdfWidth - (2 * margin);
            const availableHeight = pdfHeight - (2 * margin);
            
            const widthRatio = availableWidth / imgWidthMM;
            const heightRatio = availableHeight / imgHeightMM;
            const ratio = Math.min(widthRatio, heightRatio, 1); // Nie powiększaj, tylko zmniejszaj jeśli potrzeba
            
            const scaledWidth = imgWidthMM * ratio;
            const scaledHeight = imgHeightMM * ratio;
            
            // Wyśrodkuj obraz na stronie
            const imgX = (pdfWidth - scaledWidth) / 2;
            const imgY = (pdfHeight - scaledHeight) / 2;

            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', imgX, imgY, scaledWidth, scaledHeight);
            
            // Pobierz nazwę gołębia dla nazwy pliku
            const pigeonName = pedigreeData.main?.hodowla || pedigreeData.main?.id || 'rodowod';
            const fileName = `rodowod_${pigeonName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            
            pdf.save(fileName);
        } catch (error) {
            console.error('Błąd eksportu:', error);
            alert('Wystąpił błąd podczas eksportu rodowodu: ' + error.message);
            
            // Przywróć oryginalne style w przypadku błędu
            if (pedigreeExportRef.current) {
                pedigreeExportRef.current.style.overflow = '';
                pedigreeExportRef.current.style.maxWidth = '';
                pedigreeExportRef.current.style.maxHeight = '';
            }
        }
    };

    const renderMainPigeonCard = (pigeon, cardHeight, cardWidth, path = null, isExport = false) => {
        const effectiveCardHeight = Math.min(cardHeight, 430);
        // Zmniejszony udział zdjęcia, żeby cały kafel nie był przesadnie wysoki
        const imageHeight = Math.floor(effectiveCardHeight * 0.48);
        
        return (
            <Box 
                className="pedigree-card"
                sx={{
                    border: '2px solid #000',
                    borderRadius: 2,
                    padding: 1.5,
                    height: `${effectiveCardHeight}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    position: 'relative',
                    width: `${cardWidth}px`,
                    cursor: isExport ? 'default' : 'pointer',
                    '&:hover': isExport ? {} : {
                        borderColor: '#57C09A',
                        boxShadow: '0 2px 8px rgba(87, 192, 154, 0.3)'
                    }
                }}
                onClick={!isExport ? () => handleEditPigeon(pigeon, path) : undefined}
            >
                {/* Ikona edycji - tylko w podglądzie, nie w eksporcie */}
                {!isExport && (
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            padding: 0.5,
                            backgroundColor: 'rgba(87, 192, 154, 0.1)',
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: 'rgba(87, 192, 154, 0.2)'
                            }
                        }}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditPigeon(pigeon, path);
                        }}
                    >
                        <EditIcon sx={{ fontSize: '14px', color: '#57C09A' }} />
                    </IconButton>
                )}
                
                {/* Zdjęcie */}
                <Box sx={{
                    border: '1px solid #000',
                    borderRadius: 1,
                    padding: 0.5,
                    marginBottom: 1,
                    height: `${imageHeight}px`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden'
                }}>
                    {pigeon && pigeon.image ? (
                        <CardMedia
                            component="img"
                            image={pigeon.image}
                            alt={pigeon.name}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <Typography sx={{ color: '#999', fontFamily: "'Poppins', sans-serif", fontSize: '10px' }}>
                            Zdjęcie
                        </Typography>
                    )}
                </Box>
                {/* Logo i nazwa hodowli (domyślne) */}
                {pigeon && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', color: '#1f1f1f', textAlign: 'left', fontWeight: 700, lineHeight: 1.2 }}>
                            {pigeon.id || ''}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '13px', color: '#3f5f84', textAlign: 'left', fontWeight: 600, lineHeight: 1.2 }}>
                            {pigeon.name || ''}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#2f2f2f', textAlign: 'left', fontWeight: 600, lineHeight: 1.2 }}>
                            {pigeon.hodowla || breederData.name || ''}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '12px', color: '#c15c78', textAlign: 'left', fontWeight: 700, lineHeight: 1.2 }}>
                            {pigeon.color || ''}
                        </Typography>
                        {(pigeon.opis || pigeon.dodatkoweInformacje) && (
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '10px', color: '#2f2f2f', textAlign: 'left', lineHeight: 1.15, marginTop: 0.2 }}>
                                {pigeon.opis || pigeon.dodatkoweInformacje}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const renderPigeonCard = (pigeon, cardHeight, cardWidth, isMain = false, path = null, isExport = false, generation = 0, nodeIndex = 0) => {
        if (isMain) {
            return renderMainPigeonCard(pigeon, cardHeight, cardWidth, path, isExport);
        }
        
        // Ustandaryzowane fonty i padding - zawsze te same proporcje niezależnie od wysokości
        // Używamy proporcjonalnych wartości do wysokości karty
        const baseFont = fontSizesByGeneration[generation] ?? 9;
        const fontSize = Math.max(baseFont, Math.floor(cardHeight * 0.045));
        const smallFontSize = Math.max(baseFont - 1, Math.floor(cardHeight * 0.04));
        const padding = Math.max(0.5, Math.floor(cardHeight * 0.013)); // ~1.3% wysokości, min 0.5px
        const inferredGender = nodeIndex % 2 === 0 ? 'O' : 'M';
        
        return (
            <Box 
                className="pedigree-card"
                sx={{
                    border: '1.5px solid #000',
                    borderRadius: 1.5,
                    padding: `${padding}px`,
                    height: `${cardHeight}px`,
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    position: 'relative',
                    width: `${cardWidth}px`,
                    justifyContent: 'center',
                    cursor: isExport ? 'default' : 'pointer',
                    '&:hover': isExport ? {} : {
                        borderColor: '#57C09A',
                        boxShadow: '0 2px 8px rgba(87, 192, 154, 0.3)'
                    }
                }}
                onClick={!isExport ? () => handleEditPigeon(pigeon, path) : undefined}
            >
                {/* Ikona edycji - tylko w podglądzie, nie w eksporcie */}
                {!isExport && (
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            padding: 0.5,
                            backgroundColor: 'rgba(87, 192, 154, 0.1)',
                            zIndex: 2,
                            '&:hover': {
                                backgroundColor: 'rgba(87, 192, 154, 0.2)'
                            }
                        }}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditPigeon(pigeon, path);
                        }}
                    >
                        <EditIcon sx={{ fontSize: '12px', color: '#57C09A' }} />
                    </IconButton>
                )}
                
                {/* Literka płci w lewym górnym rogu */}
                {inferredGender && (
                    <Box sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: inferredGender === 'O' ? '#2a9d8f' : '#e07b5a',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: '11px',
                        zIndex: 1
                    }}>
                        {inferredGender}
                    </Box>
                )}
                
                {pigeon && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, width: '100%', paddingX: 0.3 }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: fontSize, color: '#1f1f1f', fontWeight: 700, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'normal', overflow: 'hidden', wordBreak: 'break-word' }}>
                            {pigeon.id || ''}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: smallFontSize, color: '#478E95', fontWeight: 700, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {truncateByGeneration(pigeon.name || '', generation)}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: Math.max(8, smallFontSize - 1), color: '#2f2f2f', fontWeight: 600, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {truncateByGeneration(pigeon.hodowla || '', generation)}
                        </Typography>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: Math.max(8, smallFontSize - 1), color: '#57C09A', fontWeight: 700, lineHeight: 1.15, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {truncateByGeneration(pigeon.color || '', generation)}
                        </Typography>
                        {(pigeon.opis || pigeon.dodatkoweInformacje) && generation <= 2 && cardHeight > 50 && (
                            <Typography
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: Math.max(7, smallFontSize - 2),
                                    textAlign: 'center',
                                    color: '#444',
                                    marginTop: 0.2,
                                    lineHeight: 1.1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical'
                                }}
                            >
                                {truncateByGeneration(pigeon.opis || pigeon.dodatkoweInformacje, generation)}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const renderGenderLabel = (gender, position = 'left') => {
        if (!gender) return null;
        return (
            <Box sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                backgroundColor: gender === 'M' ? '#478E95' : '#57C09A',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: '12.6px',
                marginLeft: position === 'right' ? 1 : 0,
                marginRight: position === 'left' ? 1 : 0,
                marginTop: position === 'bottom' ? 0.5 : 0,
                marginBottom: position === 'bottom' ? 0 : 0
            }}>
                {gender}
            </Box>
        );
    };

    return (
        <div className={styles['content-container']}>
            <Grid container spacing={0} sx={{minHeight: '100vh', margin: 0}}>
                <Grid sx={{minHeight: '100vh', height: '100%', backgroundColor: '#478E94', position: { xs: 'relative', md: 'sticky' }, top: 0, bottom: 0, alignSelf: 'flex-start', margin: 0}} item xs={12} md={2}>
                    <Card sx={{
                        borderRadius: 0,
                        backgroundColor: '#478E94',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: { xs: 1, md: 2 },
                        boxShadow: 'none',
                    }}>
                        <CardMedia
                            component="img"
                            image={Logo}
                            alt="Logo"
                            sx={{
                                width: { xs: 120, md: '50%' },
                                maxWidth: '80%',
                                height: 'auto',
                                background: '#478E94',
                            }}
                        />
                    </Card>
                    <div className={styles['all-icons-container']}>
                        {icons.map((icon, index) => (
                            <div
                                className={styles['icon-container']}
                                key={index}
                                tabIndex="0"
                                onClick={handleClick(index, iconLabels[index])}
                                style={{
                                    backgroundColor: index === 2 ? '#4c4c4c' : '#478E94'
                                }}
                            >
                                <img className={styles['custom-icon']} src={icon} alt={`Custom Icon ${index + 1}`} />
                                <p className={styles['icon-label']}>{iconLabels[index]}</p>
                            </div>
                        ))}
                    </div>
                </Grid>

                <Grid item xs={12} md={10}>
                    <div className={styles['header-container']}>
                        <div className={styles['text-header-container']}>
                            <span className={styles['text-grey-header-catalog']}>Rodowody</span>
                        </div>

                        <div className={styles['avatar-container']}>
                            <Avatar className={styles.avatar} alt="Name" src={AvatarImage} />
                            <Menu
                                id="profile-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={handleMenuClose}>Profil</MenuItem>
                                <MenuItem onClick={handleMenuClose}>Ustawienia</MenuItem>
                                <MenuItem onClick={handleLogout}>Wyloguj</MenuItem>
                            </Menu>
                            <Typography sx={{fontSize: '18px', color: '#2A2A2A', fontFamily: "'Poppins', sans-serif", fontWeight: 500}}>Krzysztof</Typography>
                            <IconButton color="inherit" onClick={handleMenuOpen}>
                                <ExpandMoreIcon style={{fontSize: '3.6vh'}}/>
                            </IconButton>
                        </div>
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: { xs: '16px', md: '20px 75px' }, gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={handleOpenExportPreview}
                                disabled={!pedigreeData}
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    textTransform: 'none',
                                    backgroundColor: '#57C09A',
                                    padding: '10px 25px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '20px',
                                    '&:hover': {
                                        backgroundColor: '#4fcda2',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#cccccc',
                                        color: '#666666'
                                    }
                                }}
                            >
                                Pobierz rodowód
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => window.print()}
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    textTransform: 'none',
                                    borderColor: '#478E95',
                                    color: '#478E95',
                                    padding: '10px 25px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '20px',
                                    '&:hover': {
                                        borderColor: '#57C09A',
                                        backgroundColor: 'rgba(71, 142, 149, 0.08)'
                                    }
                                }}
                            >
                                Drukuj rodowód
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setOcrDialogOpen(true)}
                                sx={{
                                    fontFamily: "'Poppins', sans-serif",
                                    textTransform: 'none',
                                    backgroundColor: '#9C27B0',
                                    padding: '10px 25px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    borderRadius: '20px',
                                    '&:hover': {
                                        backgroundColor: '#7B1FA2',
                                    }
                                }}
                            >
                                Importuj rodowód (OCR)
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#478E95', fontWeight: 600 }}>
                                Liczba generacji:
                            </Typography>
                            <Button
                                variant={columns === 3 ? 'contained' : 'outlined'}
                                onClick={() => setColumns(3)}
                                sx={{
                                    minWidth: '40px',
                                    fontFamily: "'Poppins', sans-serif",
                                    backgroundColor: columns === 3 ? '#478E95' : 'transparent',
                                    borderColor: '#478E95',
                                    color: columns === 3 ? 'white' : '#478E95',
                                    '&:hover': {
                                        backgroundColor: columns === 3 ? '#57C09A' : 'rgba(71, 142, 149, 0.1)',
                                        borderColor: '#57C09A'
                                    }
                                }}
                            >
                                3
                            </Button>
                            <Button
                                variant={columns === 4 ? 'contained' : 'outlined'}
                                onClick={() => setColumns(4)}
                                sx={{
                                    minWidth: '40px',
                                    fontFamily: "'Poppins', sans-serif",
                                    backgroundColor: columns === 4 ? '#478E95' : 'transparent',
                                    borderColor: '#478E95',
                                    color: columns === 4 ? 'white' : '#478E95',
                                    '&:hover': {
                                        backgroundColor: columns === 4 ? '#57C09A' : 'rgba(71, 142, 149, 0.1)',
                                        borderColor: '#57C09A'
                                    }
                                }}
                            >
                                4
                            </Button>
                            <Button
                                variant={columns === 5 ? 'contained' : 'outlined'}
                                onClick={() => setColumns(5)}
                                sx={{
                                    minWidth: '40px',
                                    fontFamily: "'Poppins', sans-serif",
                                    backgroundColor: columns === 5 ? '#478E95' : 'transparent',
                                    borderColor: '#478E95',
                                    color: columns === 5 ? 'white' : '#478E95',
                                    '&:hover': {
                                        backgroundColor: columns === 5 ? '#57C09A' : 'rgba(71, 142, 149, 0.1)',
                                        borderColor: '#57C09A'
                                    }
                                }}
                            >
                                5
                            </Button>
                        </Box>
                    </Box>

                    {/* Główny obszar z listą gołębi i rodowodem */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, margin: { xs: '0 16px', md: '0 75px' }, marginBottom: 4, minHeight: '60vh' }}>
                        {/* Lista gołębi po lewej stronie */}
                        <Box sx={{ 
                            width: { xs: '100%', md: '300px' },
                            flexShrink: 0,
                            backgroundColor: 'white',
                            borderRadius: 2,
                            padding: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            maxHeight: { xs: '320px', md: 'calc(100vh - 300px)' },
                            overflowY: 'auto'
                        }}>
                            <Typography sx={{ 
                                fontFamily: "'Poppins', sans-serif", 
                                fontWeight: 600, 
                                fontSize: '18px', 
                                color: '#478E95',
                                marginBottom: 2
                            }}>
                                Moje gołębie
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {pigeonsList.length === 0 ? (
                                    <Typography sx={{ 
                                        fontFamily: "'Poppins', sans-serif",
                                        fontSize: '12.6px',
                                        color: '#707070',
                                        textAlign: 'center',
                                        padding: 2
                                    }}>
                                        Brak gołębi
                                    </Typography>
                                ) : (
                                    pigeonsList.map((pigeon) => (
                                        <Box
                                            key={pigeon.ID}
                                            onClick={() => setSelectedPigeonId(pigeon.ID)}
                                            sx={{
                                                padding: '12px 16px',
                                                borderRadius: 2,
                                                backgroundColor: selectedPigeonId === pigeon.ID ? '#57C09A' : '#f5f5f5',
                                                color: selectedPigeonId === pigeon.ID ? 'white' : '#2A2A2A',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                border: selectedPigeonId === pigeon.ID ? '2px solid #57C09A' : '2px solid transparent',
                                                '&:hover': {
                                                    backgroundColor: selectedPigeonId === pigeon.ID ? '#4fcda2' : '#e0e0e0',
                                                    transform: 'translateX(4px)'
                                                }
                                            }}
                                        >
                                            <Typography sx={{ 
                                                fontFamily: "'Poppins', sans-serif",
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                marginBottom: 0.5
                                            }}>
                                                {pigeon.hodowla || pigeon.name || 'Bez hodowli'}
                                            </Typography>
                                            <Typography sx={{ 
                                                fontFamily: "'Poppins', sans-serif",
                                                fontSize: '12px',
                                                opacity: selectedPigeonId === pigeon.ID ? 0.9 : 0.7
                                            }}>
                                                {pigeon.number || 'Brak numeru'}
                                            </Typography>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Box>

                        {/* Rodowód po prawej stronie */}
                        <Box className="pedigree-wrapper" sx={{ flex: 1, padding: '10px 0', overflowX: 'auto', overflowY: 'auto' }}>
                            {!pedigreeData ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", color: '#707070', fontSize: '18px' }}>
                                        Wybierz gołębia z listy po lewej stronie, aby wyświetlić jego rodowód
                                    </Typography>
                                </Box>
                            ) : (() => {
                            // Funkcja do generowania ścieżki dla gołębia na danym poziomie i pozycji
                            const getPathForPosition = (level, position) => {
                                if (level === 0) return null; // Główny gołąb
                                
                                const path = [];
                                let currentPos = position;
                                
                                // Dla poziomu 1: pozycja 0 = father, pozycja 1 = mother
                                // Dla poziomu 2: pozycja 0 = father.father, pozycja 1 = father.mother, pozycja 2 = mother.father, pozycja 3 = mother.mother
                                // Dla poziomu 3: pozycja 0-3 = father.father.*, pozycja 4-7 = father.mother.*, pozycja 8-11 = mother.father.*, pozycja 12-15 = mother.mother.*
                                for (let l = 1; l <= level; l++) {
                                    const levelSize = Math.pow(2, l);
                                    const halfSize = levelSize / 2;
                                    const isRight = currentPos >= halfSize;
                                    path.push(isRight ? 'mother' : 'father');
                                    if (l < level) {
                                        currentPos = currentPos % halfSize;
                                    }
                                }
                                
                                return path;
                            };
                            
                            // CSS Grid Layout oparty o drzewo binarne
                            // columns = głębokość przodków (nie liczba kolumn!)
                            // Dla 3: gołąb, rodzice, dziadkowie (3 kolumny)
                            // Dla 4: gołąb, rodzice, dziadkowie, pradziadkowie (4 kolumny)
                            // Dla 5: gołąb+rodzice, dziadkowie, pradziadkowie, prapradziadkowie (4 kolumny)
                            const depth = columns;
                            const a4Width = 794;
                            
                            // Dla depth=5 mamy tylko 4 kolumny (rodzice w kolumnie 0 z głównym)
                            const gridCols = depth === 5 ? 4 : depth;
                            // Liczba wierszy = 2^depth (dla depth=5: 32 wierszy)
                            const gridRows = Math.pow(2, depth);
                            const rowHeight = gridRowHeightByGenerations[depth] ?? 70;
                            
                            // Oblicz szerokości kolumn - upewnij się że mieszczą się w 794px
                            // Dla wybranej opcji "3 generacje" (columns === 3) wszystkie karty mają być tak samo szerokie i równe odstępy
                            let gap, mainColWidth, otherColWidth, viewBaseWidth, viewParentWidth;
                            
                            if (columns === 3) {
                                // Dla 3 generacji: wszystkie kolumny równej szerokości, równe odstępy
                                gap = 8; // Równe odstępy
                                const totalGapWidth = (gridCols - 1) * gap;
                                const availableWidth = a4Width - totalGapWidth - 20;
                                // Wszystkie kolumny równej szerokości
                                const colWidth = Math.floor(availableWidth / gridCols);
                                mainColWidth = colWidth;
                                otherColWidth = colWidth;
                                viewBaseWidth = Math.min(180, mainColWidth - 20);
                                viewParentWidth = Math.min(160, otherColWidth - 20); // Wszystkie karty tak samo szerokie
                            } else {
                                gap = 6;
                                const totalGapWidth = (gridCols - 1) * gap;
                                const availableWidth = a4Width - totalGapWidth - 20;
                                mainColWidth = 200;
                                otherColWidth = gridCols > 1 ? Math.floor((availableWidth - mainColWidth) / (gridCols - 1)) : 0;
                                viewBaseWidth = Math.min(180, mainColWidth - 40);
                                viewParentWidth = Math.min(130, otherColWidth - 10);
                            }
                            
                            // Szerokość etykiety płci
                            const genderLabelWidth = 38;
                            
                            // Oblicz pozycje dla kolumny 0
                            // Dla depth=5: ojciec nad, główny, matka pod (wszystko w kolumnie 0)
                            // Dla depth<=4: tylko główny w kolumnie 0, rodzice w kolumnie 1
                            let fatherStartRow, fatherEndRow, mainStartRow, mainEndRow, motherStartRow, motherEndRow;
                            let viewParentHeight, viewBaseHeight, viewMotherHeight;
                            
                            if (depth === 5) {
                                // Dla 5: ojciec, główny, matka w kolumnie 0
                                const fatherRows = Math.floor(gridRows / 4);
                                const mainRows = Math.floor(gridRows / 2);
                                const motherRows = gridRows - fatherRows - mainRows;
                                
                                fatherStartRow = 1;
                                fatherEndRow = fatherStartRow + fatherRows;
                                mainStartRow = fatherEndRow;
                                mainEndRow = mainStartRow + mainRows;
                                motherStartRow = mainEndRow;
                                motherEndRow = motherStartRow + motherRows;
                                
                                viewParentHeight = fatherRows * rowHeight - (fatherRows > 1 ? gap : 0);
                                viewBaseHeight = mainRows * rowHeight - (mainRows > 1 ? gap : 0);
                                viewMotherHeight = motherRows * rowHeight - (motherRows > 1 ? gap : 0);
                            } else {
                                // Dla depth<=4: tylko główny w kolumnie 0
                                const mainRows = gridRows;
                                mainStartRow = 1;
                                mainEndRow = mainStartRow + mainRows;
                                viewBaseHeight = mainRows * rowHeight - (mainRows > 1 ? gap : 0);
                                
                                // Ojciec i matka będą w kolumnie 1 (rodzice)
                                fatherStartRow = null;
                                fatherEndRow = null;
                                motherStartRow = null;
                                motherEndRow = null;
                                viewParentHeight = 0;
                                viewMotherHeight = 0;
                            }
                            
                            return (
                                <Box className="pedigree-grid" key={`pedigree-main-grid-${columns}`} sx={{ 
                                    position: 'relative',
                                    display: 'grid',
                                    gridTemplateColumns: `${mainColWidth}px repeat(${gridCols - 1}, ${otherColWidth}px)`,
                                    gridTemplateRows: `repeat(${gridRows}, ${rowHeight}px)`,
                                    gap: `${gap}px`,
                                    width: '100%',
                                    minWidth: `${minGridWidthByGenerations[depth] || a4Width}px`,
                                    padding: '0',
                                    backgroundColor: '#ffffff',
                                    boxSizing: 'border-box'
                                }}>
                                    {/* Kolumna 0 */}
                                    {depth === 5 ? (
                                        // Dla depth=5: ojciec, główny, matka w kolumnie 0
                                        <>
                                            {/* Ojciec */}
                                            {pedigreeData?.father ? (
                                                <Box sx={{ 
                                                    gridRow: `${fatherStartRow} / ${fatherEndRow}`,
                                                    gridColumn: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start'
                                                }}>
                                                    {renderPigeonCard(pedigreeData.father, viewParentHeight, viewParentWidth, false, ['father'], false, 1, 0)}
                                                </Box>
                                            ) : (
                                                <Box sx={{ 
                                                    gridRow: `${fatherStartRow} / ${fatherEndRow}`,
                                                    gridColumn: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start'
                                                }}>
                                                    <Box 
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: 1,
                                                            padding: 1,
                                                            height: `${viewParentHeight}px`,
                                                            width: `${viewParentWidth}px`,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            backgroundColor: '#f5f5f5',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                borderColor: '#57C09A',
                                                                boxShadow: '0 2px 8px rgba(87, 192, 154, 0.3)'
                                                            }
                                                        }}
                                                        onClick={() => handleEditPigeon({ id: '', name: '', hodowla: '', color: '', opis: '' }, ['father'])}
                                                    >
                                                        <IconButton
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 2,
                                                                right: 2,
                                                                padding: 0.3,
                                                                backgroundColor: 'rgba(87, 192, 154, 0.1)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(87, 192, 154, 0.2)'
                                                                }
                                                            }}
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditPigeon({ id: '', name: '', hodowla: '', color: '', opis: '' }, ['father']);
                                                            }}
                                                        >
                                                            <EditIcon sx={{ fontSize: '10px', color: '#57C09A' }} />
                                                        </IconButton>
                                                        <Typography sx={{ 
                                                            color: '#999', 
                                                            fontFamily: "'Poppins', sans-serif", 
                                                            fontSize: '8px' 
                                                        }}>
                                                            brak
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            
                                            {/* Główny gołąb */}
                                            {pedigreeData && (
                                                <Box sx={{ 
                                                    gridRow: `${mainStartRow} / ${mainEndRow}`,
                                                    gridColumn: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start'
                                                }}>
                                                    {renderPigeonCard(pedigreeData, viewBaseHeight, viewBaseWidth, true, null, false, 0, 0)}
                                                </Box>
                                            )}
                                            
                                            {/* Matka */}
                                            {pedigreeData?.mother ? (
                                                <Box sx={{ 
                                                    gridRow: `${motherStartRow} / ${motherEndRow}`,
                                                    gridColumn: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start'
                                                }}>
                                                    {renderPigeonCard(pedigreeData.mother, viewMotherHeight, viewParentWidth, false, ['mother'], false, 1, 1)}
                                                </Box>
                                            ) : (
                                                <Box sx={{ 
                                                    gridRow: `${motherStartRow} / ${motherEndRow}`,
                                                    gridColumn: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start'
                                                }}>
                                                    <Box 
                                                        sx={{
                                                            border: '1px solid #ccc',
                                                            borderRadius: 1,
                                                            padding: 1,
                                                            height: `${viewMotherHeight}px`,
                                                            width: `${viewParentWidth}px`,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            backgroundColor: '#f5f5f5',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                borderColor: '#57C09A',
                                                                boxShadow: '0 2px 8px rgba(87, 192, 154, 0.3)'
                                                            }
                                                        }}
                                                        onClick={() => handleEditPigeon({ id: '', name: '', hodowla: '', color: '', opis: '' }, ['mother'])}
                                                    >
                                                        <IconButton
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 2,
                                                                right: 2,
                                                                padding: 0.3,
                                                                backgroundColor: 'rgba(87, 192, 154, 0.1)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(87, 192, 154, 0.2)'
                                                                }
                                                            }}
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditPigeon({ id: '', name: '', hodowla: '', color: '', opis: '' }, ['mother']);
                                                            }}
                                                        >
                                                            <EditIcon sx={{ fontSize: '10px', color: '#57C09A' }} />
                                                        </IconButton>
                                                        <Typography sx={{ 
                                                            color: '#999', 
                                                            fontFamily: "'Poppins', sans-serif", 
                                                            fontSize: '8px' 
                                                        }}>
                                                            brak
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        // Dla depth<=4: tylko główny w kolumnie 0
                                        <>
                                            {/* Główny gołąb */}
                                            {pedigreeData && (
                                                <Box sx={{ 
                                                    gridRow: `${mainStartRow} / ${mainEndRow}`,
                                                    gridColumn: '1',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start'
                                                }}>
                                                    {renderPigeonCard(pedigreeData, viewBaseHeight, viewBaseWidth, true, null, false, 0, 0)}
                                                </Box>
                                            )}
                                        </>
                                    )}
                                    
                                    {/* Pozostałe kolumny */}
                                    {Array.from({ length: gridCols - 1 }, (_, colIndex) => {
                                        // Dla depth=5: kolumna 1 = poziom 2 (dziadkowie), kolumna 2 = poziom 3, kolumna 3 = poziom 4
                                        // Dla depth<=4: kolumna 1 = poziom 1 (rodzice), kolumna 2 = poziom 2 (dziadkowie), itd.
                                        const level = depth === 5 ? colIndex + 2 : colIndex + 1;
                                        const maxPigeonsAtLevel = Math.pow(2, level);
                                        const col = colIndex + 2; // Kolumna 2, 3, 4... (kolumna 1 to główna)
                                        
                                        // Każdy gołąb zajmuje dokładnie równą część z 2^depth wierszy
                                        const exactRowsPerPigeon = gridRows / maxPigeonsAtLevel;
                                        
                                        return (
                                            <React.Fragment key={colIndex}>
                                                {Array.from({ length: maxPigeonsAtLevel }, (_, index) => {
                                                    const path = getPathForPosition(level, index);
                                                    const pigeon = getPigeonByPath(pedigreeData, path);
                                                    // Dokładne obliczenie pozycji - każdy gołąb zajmuje dokładnie exactRowsPerPigeon wierszy
                                                    // Dla równomiernego rozmieszczenia używamy dokładnych wartości
                                                    const rowsPerNode = gridRows / Math.pow(2, level);
                                                    const startRow = index * rowsPerNode + 1;
                                                    const rowSpan = rowsPerNode;
                                                    // Wysokość karty = rowSpan * rowHeight - (rowSpan - 1) * gap (gap między każdym wierszem)
                                                    const cardHeight = rowSpan * rowHeight - (rowSpan - 1) * gap;
                                                    
                                                    if (!pigeon) {
                                                        const emptyPigeon = { id: '', name: '', hodowla: '', color: '', opis: '' };
                                                        return (
                                                            <Box 
                                                                key={index}
                                                                sx={{ 
                                                                    gridRow: `${startRow} / ${startRow + rowSpan}`,
                                                                    gridColumn: col,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'flex-start'
                                                                }}
                                                            >
                                                                <Box 
                                                                    sx={{
                                                                        border: '1px solid #ccc',
                                                                        borderRadius: 1,
                                                                        padding: level <= 2 ? 1 : 0.5,
                                                                        height: `${cardHeight}px`,
                                                                        width: `${viewParentWidth}px`,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        backgroundColor: '#f5f5f5',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center',
                                                                        position: 'relative',
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            borderColor: '#57C09A',
                                                                            boxShadow: '0 2px 8px rgba(87, 192, 154, 0.3)'
                                                                        }
                                                                    }}
                                                                    onClick={() => handleEditPigeon(emptyPigeon, path)}
                                                                >
                                                                    <IconButton
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: 2,
                                                                            right: 2,
                                                                            padding: 0.3,
                                                                            backgroundColor: 'rgba(87, 192, 154, 0.1)',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(87, 192, 154, 0.2)'
                                                                            }
                                                                        }}
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEditPigeon(emptyPigeon, path);
                                                                        }}
                                                                    >
                                                                        <EditIcon sx={{ fontSize: level <= 2 ? '10px' : '8px', color: '#57C09A' }} />
                                                                    </IconButton>
                                                                    <Typography sx={{ 
                                                                        color: '#999', 
                                                                        fontFamily: "'Poppins', sans-serif", 
                                                                        fontSize: level <= 2 ? '8px' : '6px' 
                                                                    }}>
                                                                        brak
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    }
                                                    
                                                    return (
                                                        <Box 
                                                            key={pigeon.id || index}
                                                            sx={{ 
                                                                gridRow: `${startRow} / ${startRow + rowSpan}`,
                                                                gridColumn: col,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-start'
                                                            }}
                                                        >
                                                            {renderPigeonCard(pigeon, cardHeight, viewParentWidth, false, path, false, level, index)}
                                                        </Box>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                    
                                    {/* SVG Overlay z liniami łączącymi generacje - tymczasowo wyłączone */}
                                    {/* 
                                    <svg
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: `${a4Width}px`,
                                            height: `${availableHeight}px`,
                                            pointerEvents: 'none',
                                            zIndex: 1
                                        }}
                                        viewBox={`0 0 ${a4Width} ${availableHeight}`}
                                    >
                                        Linie łączące generacje
                                        {(() => {
                                            const lines = [];
                                            
                                            // Oblicz pozycje kart - zwraca środek karty
                                            const getCardCenter = (col, startRow, endRow, cardWidth) => {
                                                // Pozycja X - środek kolumny
                                                let x;
                                                if (col === 1) {
                                                    x = mainColWidth / 2;
                                                } else {
                                                    x = mainColWidth + gap + (col - 2) * (otherColWidth + gap) + otherColWidth / 2;
                                                }
                                                
                                                // Pozycja Y - środek karty w wierszach
                                                const cardStartY = (startRow - 1) * (rowHeight + gap);
                                                const cardHeight = (endRow - startRow) * rowHeight + (endRow - startRow - 1) * gap;
                                                const y = cardStartY + cardHeight / 2;
                                                
                                                return { x, y, cardWidth };
                                            };
                                            
                                            // Dla depth=5: linie w kolumnie 0 (ojciec → główny → matka)
                                            if (depth === 5) {
                                                const mainCenter = getCardCenter(1, mainStartRow, mainEndRow, viewBaseWidth);
                                                const fatherCenter = getCardCenter(1, fatherStartRow, fatherEndRow, viewParentWidth);
                                                const motherCenter = getCardCenter(1, motherStartRow, motherEndRow, viewParentWidth);
                                                
                                                // Linia od ojca do głównego (pionowa)
                                                lines.push(
                                                    <line
                                                        key="father-main"
                                                        x1={fatherCenter.x}
                                                        y1={fatherCenter.y + viewParentHeight / 2}
                                                        x2={mainCenter.x}
                                                        y2={mainCenter.y - viewBaseHeight / 2}
                                                        stroke="#478E95"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                );
                                                
                                                // Linia od głównego do matki (pionowa)
                                                lines.push(
                                                    <line
                                                        key="main-mother"
                                                        x1={mainCenter.x}
                                                        y1={mainCenter.y + viewBaseHeight / 2}
                                                        x2={motherCenter.x}
                                                        y2={motherCenter.y - viewMotherHeight / 2}
                                                        stroke="#478E95"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                );
                                            } else {
                                                // Dla depth<=4: linie od głównego (dziecko) do rodziców w kolumnie 2
                                                const mainCenter = getCardCenter(1, mainStartRow, mainEndRow, viewBaseWidth);
                                                
                                                // Oblicz pozycje rodziców w kolumnie 2 (poziom 1)
                                                const level1Rows = gridRows / 2; // 2 rodziców, każdy zajmuje połowę wierszy
                                                const fatherRow1 = 1;
                                                const fatherRow2 = Math.floor(level1Rows);
                                                const motherRow1 = Math.floor(level1Rows) + 1;
                                                const motherRow2 = gridRows;
                                                
                                                const fatherCenter = getCardCenter(2, fatherRow1, fatherRow2, viewParentWidth);
                                                const motherCenter = getCardCenter(2, motherRow1, motherRow2, viewParentWidth);
                                                
                                                // Dwie poziome linie od głównego (dziecko) - zaczynają się od prawej krawędzi głównego
                                                const mainLineStartX = mainCenter.x + viewBaseWidth / 2; // Prawa krawędź głównego
                                                
                                                // Linia od głównego do ojca - pozioma na wysokości ojca
                                                lines.push(
                                                    <line
                                                        key="main-father"
                                                        x1={mainLineStartX}
                                                        y1={fatherCenter.y}
                                                        x2={fatherCenter.x - viewParentWidth / 2}
                                                        y2={fatherCenter.y}
                                                        stroke="#478E95"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                );
                                                
                                                // Linia od głównego do matki - pozioma na wysokości matki
                                                lines.push(
                                                    <line
                                                        key="main-mother"
                                                        x1={mainLineStartX}
                                                        y1={motherCenter.y}
                                                        x2={motherCenter.x - viewParentWidth / 2}
                                                        y2={motherCenter.y}
                                                        stroke="#478E95"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                );
                                            }
                                            
                                            // Linie dla pozostałych poziomów (dziadkowie, pradziadkowie, itd.)
                                            // Dla każdego dziecka rysujemy 2 linie do jego rodziców (tylko jeśli nie jest to pierwsza kolumna)
                                            {Array.from({ length: gridCols - 1 }, (_, colIndex) => {
                                                const level = depth === 5 ? colIndex + 2 : colIndex + 1;
                                                const col = colIndex + 2; // Kolumna dzieci w gridzie
                                                const parentCol = col - 1; // Kolumna rodziców
                                                
                                                // Nie rysuj linii z pierwszej kolumny (główny gołąb już obsłużony)
                                                if (col <= 1) return null;
                                                
                                                if (parentCol < 1) return null; // Brak rodziców
                                                
                                                const childLevel = level;
                                                const childMaxPigeons = Math.pow(2, childLevel);
                                                const childExactRowsPerPigeon = gridRows / childMaxPigeons;
                                                
                                                return Array.from({ length: childMaxPigeons }, (_, childIndex) => {
                                                    // Oblicz pozycję dziecka
                                                    const childStartRow = Math.floor(childIndex * childExactRowsPerPigeon) + 1;
                                                    const childEndRow = Math.floor((childIndex + 1) * childExactRowsPerPigeon) + 1;
                                                    const childCenter = getCardCenter(col, childStartRow, childEndRow, viewParentWidth);
                                                    
                                                    // Każde dziecko ma 2 rodziców (ojciec i matka)
                                                    const parentIndex = Math.floor(childIndex / 2);
                                                    const parentLevel = level - 1;
                                                    const parentMaxPigeons = Math.pow(2, parentLevel);
                                                    const parentExactRowsPerPigeon = gridRows / parentMaxPigeons;
                                                    
                                                    // Pozycje rodziców
                                                    const parentStartRow = Math.floor(parentIndex * parentExactRowsPerPigeon) + 1;
                                                    const parentEndRow = Math.floor((parentIndex + 1) * parentExactRowsPerPigeon) + 1;
                                                    const parentCenter = getCardCenter(parentCol, parentStartRow, parentEndRow, 
                                                        parentCol === 1 ? viewBaseWidth : viewParentWidth);
                                                    
                                                    // Oblicz pozycje ojca i matki (ojciec górny, matka dolna)
                                                    const parentCardWidth = parentCol === 1 ? viewBaseWidth : viewParentWidth;
                                                    const parentRows = parentEndRow - parentStartRow + 1;
                                                    const fatherRows = Math.floor(parentRows / 2);
                                                    
                                                    const fatherStartRow = parentStartRow;
                                                    const fatherEndRow = parentStartRow + fatherRows - 1;
                                                    const motherStartRow = parentStartRow + fatherRows;
                                                    const motherEndRow = parentEndRow;
                                                    
                                                    const fatherCenter = getCardCenter(parentCol, fatherStartRow, fatherEndRow, parentCardWidth);
                                                    const motherCenter = getCardCenter(parentCol, motherStartRow, motherEndRow, parentCardWidth);
                                                    
                                                    // Dwie poziome linie od dziecka - zaczynają się od prawej krawędzi dziecka
                                                    const childLineStartX = childCenter.x + viewParentWidth / 2; // Prawa krawędź dziecka
                                                    
                                                    return (
                                                        <React.Fragment key={`child-${col}-${childIndex}`}>
                                                            <line
                                                                key={`line-${col}-${childIndex}-father`}
                                                                x1={childLineStartX}
                                                                y1={fatherCenter.y}
                                                                x2={fatherCenter.x - parentCardWidth / 2}
                                                                y2={fatherCenter.y}
                                                                stroke="#478E95"
                                                                strokeWidth="1.5"
                                                                fill="none"
                                                            />
                                                            <line
                                                                key={`line-${col}-${childIndex}-mother`}
                                                                x1={childLineStartX}
                                                                y1={motherCenter.y}
                                                                x2={motherCenter.x - parentCardWidth / 2}
                                                                y2={motherCenter.y}
                                                                stroke="#478E95"
                                                                strokeWidth="1.5"
                                                                fill="none"
                                                            />
                                                        </React.Fragment>
                                                    );
                                                });
                                            })}
                                            
                                            return lines;
                                        })()}
                                    </svg> */}
                                </Box>
                            );
                        })()}
                        
                        {/* Ukryty element do eksportu PDF z nowym layoutem */}
                        {pedigreeData && (() => {
                            // CSS Grid Layout oparty o drzewo binarne
                            const depth = columns;
                            const a4Width = 794;
                            // Wysokość dostępna na A4 po odjęciu nagłówka (~100px), stopki (40px) i paddingów (30px)
                            // Dla większej liczby kolumn (większej głębokości) zmniejszamy dostępną wysokość
                            const availableHeight = getPedigreeGridContentHeightPx(depth);
                            const gridRows = Math.pow(2, depth); // Liczba wierszy = 2^depth
                            // Dla depth=5 mamy tylko 4 kolumny (rodzice w kolumnie 0 z głównym)
                            const gridCols = depth === 5 ? 4 : depth;
                            
                            // Oblicz szerokości kolumn - upewnij się że mieszczą się w 794px
                            // Dla wybranej opcji "3 generacje" (columns === 3) wszystkie karty mają być tak samo szerokie i równe odstępy
                            let gap, mainColWidth, otherColWidth, exportBaseWidth, exportParentWidth;
                            
                            if (columns === 3) {
                                // Dla 3 generacji: wszystkie kolumny równej szerokości, równe odstępy
                                gap = 8; // Równe odstępy
                                const totalGapWidth = (gridCols - 1) * gap;
                                const availableWidth = a4Width - totalGapWidth - 20;
                                // Wszystkie kolumny równej szerokości
                                const colWidth = Math.floor(availableWidth / gridCols);
                                mainColWidth = colWidth;
                                otherColWidth = colWidth;
                                exportBaseWidth = Math.min(180, mainColWidth - 20);
                                exportParentWidth = Math.min(160, otherColWidth - 20); // Wszystkie karty tak samo szerokie
                            } else {
                                gap = 6;
                                const totalGapWidth = (gridCols - 1) * gap;
                                const availableWidth = a4Width - totalGapWidth - 20;
                                mainColWidth = 200;
                                otherColWidth = gridCols > 1 ? Math.floor((availableWidth - mainColWidth) / (gridCols - 1)) : 0;
                                exportBaseWidth = Math.min(180, mainColWidth - 40);
                                exportParentWidth = Math.min(130, otherColWidth - 10);
                            }
                            
                            const rowHeight = (availableHeight - (gridRows - 1) * gap) / gridRows; // Uwzględnij odstępy, aby nie nachodziło na stopkę
                            
                            // Szerokość etykiety płci
                            const exportGenderLabelWidth = 38;
                            
                            // Oblicz pozycje dla kolumny 0
                            // Dla depth=5: ojciec nad, główny, matka pod (wszystko w kolumnie 0)
                            // Dla depth<=4: tylko główny w kolumnie 0, rodzice w kolumnie 1
                            let fatherStartRow, fatherEndRow, mainStartRow, mainEndRow, motherStartRow, motherEndRow;
                            let exportParentHeight, exportBaseHeight, exportMotherHeight;
                            
                            if (depth === 5) {
                                // Dla 5: ojciec, główny, matka w kolumnie 0
                                const fatherRows = Math.floor(gridRows / 4);
                                const mainRows = Math.floor(gridRows / 2);
                                const motherRows = gridRows - fatherRows - mainRows;
                                
                                fatherStartRow = 1;
                                fatherEndRow = fatherStartRow + fatherRows;
                                mainStartRow = fatherEndRow;
                                mainEndRow = mainStartRow + mainRows;
                                motherStartRow = mainEndRow;
                                motherEndRow = motherStartRow + motherRows;
                                
                                exportParentHeight = fatherRows * rowHeight - (fatherRows > 1 ? gap : 0);
                                exportBaseHeight = mainRows * rowHeight - (mainRows > 1 ? gap : 0);
                                exportMotherHeight = motherRows * rowHeight - (motherRows > 1 ? gap : 0);
                            } else {
                                // Dla depth<=4: tylko główny w kolumnie 0
                                const mainRows = gridRows;
                                mainStartRow = 1;
                                mainEndRow = mainStartRow + mainRows;
                                exportBaseHeight = mainRows * rowHeight - (mainRows > 1 ? gap : 0);
                                
                                // Ojciec i matka będą w kolumnie 1 (rodzice)
                                fatherStartRow = null;
                                fatherEndRow = null;
                                motherStartRow = null;
                                motherEndRow = null;
                                exportParentHeight = 0;
                                exportMotherHeight = 0;
                            }
                            
                            const exportGetPathForPosition = (level, position) => {
                                if (level === 0) return null;
                                const path = [];
                                let currentPos = position;
                                for (let l = 1; l <= level; l++) {
                                    const levelSize = Math.pow(2, l);
                                    const halfSize = levelSize / 2;
                                    const isRight = currentPos >= halfSize;
                                    path.push(isRight ? 'mother' : 'father');
                                    if (l < level) {
                                        currentPos = currentPos % halfSize;
                                    }
                                }
                                return path;
                            };
                            
                            return (
                                <Box 
                                    ref={pedigreeExportRef} 
                                    sx={{ 
                                        width: '210mm',
                                        minHeight: '297mm',
                                        backgroundColor: '#ffffff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        fontFamily: "'Poppins', sans-serif",
                                        position: 'absolute',
                                        left: '-9999px',
                                        top: 0
                                    }}
                                >
                                {/* Nagłówek */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start',
                                    padding: '15px 20px',
                                    borderBottom: '1px solid #333'
                                }}>
                                    <Box>
                                        <Typography sx={{ 
                                            fontSize: '28px', 
                                            fontWeight: 700, 
                                            color: '#333',
                                            marginBottom: '8px'
                                        }}>
                                            {breederData.name || 'Nazwa hodowli'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                                            {profileData.firstName} {profileData.lastName}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                                            {profileData.city}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#333' }}>
                                            {profileData.phone}
                                        </Typography>
                                    </Box>
                                    {breederData.logo && (
                                        <Box sx={{ maxWidth: '150px', maxHeight: '80px' }}>
                                            <Box 
                                                component="img"
                                                src={breederData.logo}
                                                alt="Logo hodowli"
                                                sx={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                
                                {/* Główna sekcja - tylko drzewo genealogiczne */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flex: 1,
                                    padding: '15px 10px',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start'
                                }}>
                                    {/* Drzewo genealogiczne - CSS Grid */}
                                <Box className="pedigree-grid" sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: `${mainColWidth}px repeat(${gridCols - 1}, ${otherColWidth}px)`,
                                        gridTemplateRows: `repeat(${gridRows}, ${rowHeight}px)`,
                                        gap: `${gap}px`,
                                        width: `${a4Width}px`,
                                        height: `${availableHeight}px`,
                                        padding: '0',
                                        backgroundColor: '#ffffff',
                                        boxSizing: 'border-box'
                                    }}>
                                        {/* Kolumna 0 */}
                                        {depth === 5 ? (
                                            // Dla depth=5: ojciec, główny, matka w kolumnie 0
                                            <>
                                                {/* Ojciec */}
                                                {pedigreeData?.father ? (
                                                    <Box sx={{ 
                                                        gridRow: `${fatherStartRow} / ${fatherEndRow}`,
                                                        gridColumn: '1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start'
                                                    }}>
                                                        {renderPigeonCard(pedigreeData.father, exportParentHeight, exportParentWidth, false, ['father'], true, 1, 0)}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ 
                                                        gridRow: `${fatherStartRow} / ${fatherEndRow}`,
                                                        gridColumn: '1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start'
                                                    }}>
                                                        <Box sx={{ 
                                                            border: '1px solid #ccc',
                                                            borderRadius: 1,
                                                            padding: 1,
                                                            height: `${exportParentHeight}px`,
                                                            width: `${exportParentWidth}px`,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            backgroundColor: '#f5f5f5',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            <Typography sx={{ color: '#999', fontFamily: "'Poppins', sans-serif", fontSize: '8px' }}>
                                                                brak
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                
                                                {/* Główny gołąb */}
                                                {pedigreeData && (
                                                    <Box sx={{ 
                                                        gridRow: `${mainStartRow} / ${mainEndRow}`,
                                                        gridColumn: '1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start'
                                                    }}>
                                                        {renderPigeonCard(pedigreeData, exportBaseHeight, exportBaseWidth, true, null, true, 0, 0)}
                                                    </Box>
                                                )}
                                                
                                                {/* Matka */}
                                                {pedigreeData?.mother ? (
                                                    <Box sx={{ 
                                                        gridRow: `${motherStartRow} / ${motherEndRow}`,
                                                        gridColumn: '1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start'
                                                    }}>
                                                        {renderPigeonCard(pedigreeData.mother, exportMotherHeight, exportParentWidth, false, ['mother'], true, 1, 1)}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ 
                                                        gridRow: `${motherStartRow} / ${motherEndRow}`,
                                                        gridColumn: '1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start'
                                                    }}>
                                                        <Box sx={{ 
                                                            border: '1px solid #ccc',
                                                            borderRadius: 1,
                                                            padding: 1,
                                                            height: `${exportMotherHeight}px`,
                                                            width: `${exportParentWidth}px`,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            backgroundColor: '#f5f5f5',
                                                            justifyContent: 'center',
                                                            alignItems: 'center'
                                                        }}>
                                                            <Typography sx={{ color: '#999', fontFamily: "'Poppins', sans-serif", fontSize: '8px' }}>
                                                                brak
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            // Dla depth<=4: tylko główny w kolumnie 0
                                            <>
                                                {/* Główny gołąb */}
                                                {pedigreeData && (
                                                    <Box sx={{ 
                                                        gridRow: `${mainStartRow} / ${mainEndRow}`,
                                                        gridColumn: '1',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start'
                                                    }}>
                                                        {renderPigeonCard(pedigreeData, exportBaseHeight, exportBaseWidth, true, null, true, 0, 0)}
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                        
                                        {/* Pozostałe kolumny */}
                                        {Array.from({ length: gridCols - 1 }, (_, colIndex) => {
                                            // Dla depth=5: kolumna 1 = poziom 2 (dziadkowie), kolumna 2 = poziom 3, kolumna 3 = poziom 4
                                            // Dla depth<=4: kolumna 1 = poziom 1 (rodzice), kolumna 2 = poziom 2 (dziadkowie), itd.
                                            const level = depth === 5 ? colIndex + 2 : colIndex + 1;
                                            const maxPigeonsAtLevel = Math.pow(2, level);
                                            const col = colIndex + 2; // Kolumna 2, 3, 4... (kolumna 1 to główna)
                                            
                                            // Każdy gołąb zajmuje dokładnie równą część z 2^depth wierszy
                                            const exactRowsPerPigeon = gridRows / maxPigeonsAtLevel;
                                            
                                            return (
                                                <React.Fragment key={colIndex}>
                                                    {Array.from({ length: maxPigeonsAtLevel }, (_, index) => {
                                                        const path = exportGetPathForPosition(level, index);
                                                        const pigeon = getPigeonByPath(pedigreeData, path);
                                                        // Dokładne obliczenie pozycji - każdy gołąb zajmuje dokładnie exactRowsPerPigeon wierszy
                                                        // Dla równomiernego rozmieszczenia używamy dokładnych wartości
                                                        const rowsPerNode = gridRows / Math.pow(2, level);
                                                        const startRow = index * rowsPerNode + 1;
                                                        const rowSpan = rowsPerNode;
                                                        // Wysokość karty = rowSpan * rowHeight - (rowSpan - 1) * gap (gap między każdym wierszem)
                                                        const cardHeight = rowSpan * rowHeight - (rowSpan - 1) * gap;
                                                        
                                                        if (!pigeon) {
                                                            return (
                                                                <Box 
                                                                    key={index}
                                                                    sx={{ 
                                                                        gridRow: `${startRow} / ${startRow + rowSpan}`,
                                                                        gridColumn: col,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}
                                                                >
                                                                    <Box sx={{
                                                                        border: '1px solid #ccc',
                                                                        borderRadius: 1,
                                                                        padding: level <= 2 ? 1 : 0.5,
                                                                        height: `${cardHeight}px`,
                                                                        width: `${exportParentWidth}px`,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        backgroundColor: '#f5f5f5',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'center'
                                                                    }}>
                                                                        <Typography sx={{ 
                                                                            color: '#999', 
                                                                            fontFamily: "'Poppins', sans-serif", 
                                                                            fontSize: level <= 2 ? '8px' : '6px' 
                                                                        }}>
                                                                            brak
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        }
                                                        
                                                        
                                                        return (
                                                            <Box 
                                                                key={pigeon.id || index}
                                                                sx={{ 
                                                                    gridRow: `${startRow} / ${startRow + rowSpan}`,
                                                                    gridColumn: col,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'flex-start'
                                                                }}
                                                            >
                                                                {renderPigeonCard(pigeon, cardHeight, exportParentWidth, false, path, true, level, index)}
                                                            </Box>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </Box>
                                </Box>
                                
                                {/* Stopka */}
                                <Box sx={{ 
                                    backgroundColor: '#57C09A',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '0 20px'
                                }}>
                                    <Typography sx={{ 
                                        color: 'white', 
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}>
                                        Lotnik 360
                                    </Typography>
                                    <Box component="img" src={Logo} alt="Lotnik 360" sx={{ height: 22, width: 'auto', objectFit: 'contain' }} />
                                </Box>
                            </Box>
                            );
                        })()}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Dialog do wczytywania i przetwarzania OCR */}
            <Dialog open={ocrDialogOpen} onClose={handleCloseOcrDialog} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Importuj rodowód przez OCR
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                        {!selectedPigeonId && (
                            <Typography sx={{ color: 'error.main', fontFamily: "'Poppins', sans-serif" }}>
                                Najpierw wybierz gołębia z listy po lewej stronie!
                            </Typography>
                        )}
                        
                        
                        <Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                style={{ display: 'none' }}
                                id="ocr-image-upload"
                            />
                            <label htmlFor="ocr-image-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    fullWidth
                                    sx={{
                                        fontFamily: "'Poppins', sans-serif",
                                        textTransform: 'none',
                                        borderColor: '#57C09A',
                                        color: '#57C09A',
                                        '&:hover': {
                                            borderColor: '#4fcda2',
                                            backgroundColor: 'rgba(87, 192, 154, 0.1)'
                                        }
                                    }}
                                >
                                    Wczytaj zdjęcie rodowodu
                                </Button>
                            </label>
                        </Box>


                        {ocrImage && (
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, marginBottom: 1 }}>
                                    Podgląd zdjęcia:
                                </Typography>
                                <Box
                                    component="img"
                                    src={ocrImage}
                                    alt="Rodowód"
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2
                                    }}
                                />
                            </Box>
                        )}

                        {ocrLoading && (
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", marginBottom: 1 }}>
                                    Przetwarzanie OCR... {ocrProgress}%
                                </Typography>
                                <LinearProgress variant="determinate" value={ocrProgress} />
                            </Box>
                        )}

                        {ocrResult && (
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, marginBottom: 1 }}>
                                    Rozpoznany tekst ({ocrResult.length} znaków):
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={10}
                                    value={ocrResult}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontFamily: "'Poppins', sans-serif",
                                        }
                                    }}
                                    InputProps={{
                                        readOnly: true
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button 
                        onClick={handleCloseOcrDialog}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button 
                        onClick={handleOCRProcess}
                        variant="contained"
                        disabled={!ocrImage || !selectedPigeonId || ocrLoading}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': {
                                backgroundColor: '#4fcda2',
                            },
                            '&:disabled': {
                                backgroundColor: '#ccc'
                            }
                        }}
                    >
                        {ocrLoading ? 'Przetwarzanie...' : 'Przetwórz OCR'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog do edycji danych z OCR */}
            <Dialog open={ocrEditDialogOpen} onClose={() => setOcrEditDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Edytuj dane z zaimportowanego rodowodu
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', color: '#478E95' }}>
                            Główny gołąb
                        </Typography>
                        <TextField
                            fullWidth
                            label="Hodowla"
                            value={ocrParsedData?.main?.hodowla || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    main: { ...(prev.main || {}), hodowla: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Nr obrączki"
                            value={ocrParsedData?.main?.id || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    main: { ...(prev.main || {}), id: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel sx={{ fontFamily: "'Poppins', sans-serif" }}>Płeć</InputLabel>
                            <Select
                                value={ocrParsedData?.main?.gender || ''}
                                onChange={(e) => setOcrParsedData(prev => {
                                    if (!prev) return prev;
                                    return {
                                        ...prev,
                                        main: { ...(prev.main || {}), gender: e.target.value }
                                    };
                                })}
                                label="Płeć"
                                sx={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                                <MenuItem value="M">Samiec (M)</MenuItem>
                                <MenuItem value="O">Samica (O)</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Barwa"
                            value={ocrParsedData?.main?.color || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    main: { ...(prev.main || {}), color: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Status"
                            value={ocrParsedData?.main?.status || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    main: { ...(prev.main || {}), status: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Opis"
                            multiline
                            rows={2}
                            value={ocrParsedData?.main?.opis || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    main: { ...(prev.main || {}), opis: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Dodatkowe informacje"
                            multiline
                            rows={2}
                            value={ocrParsedData?.main?.dodatkoweInformacje || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    main: { ...(prev.main || {}), dodatkoweInformacje: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', color: '#478E95', marginTop: 2 }}>
                            Ojciec
                        </Typography>
                        <TextField
                            fullWidth
                            label="Hodowla"
                            value={ocrParsedData?.father?.hodowla || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    father: { ...(prev.father || {}), hodowla: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Nr obrączki"
                            value={ocrParsedData?.father?.id || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    father: { ...(prev.father || {}), id: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Barwa"
                            value={ocrParsedData?.father?.color || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    father: { ...(prev.father || {}), color: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Status"
                            value={ocrParsedData?.father?.status || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    father: { ...(prev.father || {}), status: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '18px', color: '#478E95', marginTop: 2 }}>
                            Matka
                        </Typography>
                        <TextField
                            fullWidth
                            label="Hodowla"
                            value={ocrParsedData?.mother?.hodowla || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    mother: { ...(prev.mother || {}), hodowla: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Nr obrączki"
                            value={ocrParsedData?.mother?.id || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    mother: { ...(prev.mother || {}), id: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Barwa"
                            value={ocrParsedData?.mother?.color || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    mother: { ...(prev.mother || {}), color: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Status"
                            value={ocrParsedData?.mother?.status || ''}
                            onChange={(e) => setOcrParsedData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    mother: { ...(prev.mother || {}), status: e.target.value }
                                };
                            })}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button 
                        onClick={() => setOcrEditDialogOpen(false)}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button 
                        onClick={() => {
                            if (!ocrParsedData) {
                                alert('Błąd: Brak danych z OCR');
                                return;
                            }
                            if (!pedigreeData) {
                                alert('Błąd: Brak danych rodowodu');
                                return;
                            }
                            if (!selectedPigeonId) {
                                alert('Błąd: Nie wybrano gołębia');
                                return;
                            }

                            const main = ocrParsedData.main || {};
                            const father = ocrParsedData.father || {};
                            const mother = ocrParsedData.mother || {};
                            
                            // Funkcja pomocnicza do wyboru wartości
                            const getValue = (newVal, oldVal, fallback = '') => {
                                return (newVal !== undefined && newVal !== null && newVal !== '') ? newVal : (oldVal || fallback);
                            };
                            
                            const updatedPedigree = {
                                ...pedigreeData,
                                hodowla: getValue(main.hodowla, pedigreeData.hodowla || pedigreeData.name),
                                id: getValue(main.id, pedigreeData.id),
                                gender: getValue(main.gender, pedigreeData.gender),
                                color: getValue(main.color, pedigreeData.color),
                                status: getValue(main.status, pedigreeData.status),
                                opis: getValue(main.opis, pedigreeData.opis),
                                dodatkoweInformacje: getValue(main.dodatkoweInformacje, pedigreeData.dodatkoweInformacje),
                                father: (father.id || father.hodowla) ? {
                                    ...(pedigreeData.father || {}),
                                    hodowla: getValue(father.hodowla, pedigreeData.father?.hodowla || pedigreeData.father?.name),
                                    id: getValue(father.id, pedigreeData.father?.id),
                                    gender: 'M',
                                    color: getValue(father.color, pedigreeData.father?.color),
                                    status: getValue(father.status, pedigreeData.father?.status),
                                    opis: getValue(father.opis, pedigreeData.father?.opis),
                                    dodatkoweInformacje: getValue(father.dodatkoweInformacje, pedigreeData.father?.dodatkoweInformacje)
                                } : pedigreeData.father,
                                mother: (mother.id || mother.hodowla) ? {
                                    ...(pedigreeData.mother || {}),
                                    hodowla: getValue(mother.hodowla, pedigreeData.mother?.hodowla || pedigreeData.mother?.name),
                                    id: getValue(mother.id, pedigreeData.mother?.id),
                                    gender: 'O',
                                    color: getValue(mother.color, pedigreeData.mother?.color),
                                    status: getValue(mother.status, pedigreeData.mother?.status),
                                    opis: getValue(mother.opis, pedigreeData.mother?.opis),
                                    dodatkoweInformacje: getValue(mother.dodatkoweInformacje, pedigreeData.mother?.dodatkoweInformacje)
                                } : pedigreeData.mother
                            };
                            
                            console.log('Zapisywanie rodowodu:', updatedPedigree);
                            setPedigreeData(updatedPedigree);
                            savePedigreeForPigeon(selectedPigeonId, updatedPedigree);
                            setOcrEditDialogOpen(false);
                            setOcrParsedData(null);
                            alert('Rodowód został zaktualizowany!');
                        }}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': {
                                backgroundColor: '#4fcda2',
                            }
                        }}
                    >
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog do edycji gołębia w rodowodzie */}
            <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Edytuj gołębia w rodowodzie
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                        <TextField
                            fullWidth
                            label="Nr obrączki"
                            value={editForm.id}
                            onChange={(e) => setEditForm(prev => ({ ...prev, id: e.target.value }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Nazwa"
                            value={editForm.name}
                            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Hodowca/Hodowla"
                            value={editForm.hodowla}
                            onChange={(e) => setEditForm(prev => ({ ...prev, hodowla: e.target.value }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Kolor"
                            value={editForm.color}
                            onChange={(e) => setEditForm(prev => ({ ...prev, color: e.target.value }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Dodatkowy opis"
                            multiline
                            rows={3}
                            value={editForm.opis}
                            onChange={(e) => setEditForm(prev => ({ ...prev, opis: e.target.value }))}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: "'Poppins', sans-serif",
                                }
                            }}
                        />
                        
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button 
                        onClick={handleCloseEditDialog}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button 
                        onClick={handleSaveEdit}
                        variant="contained"
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': {
                                backgroundColor: '#4fcda2',
                            }
                        }}
                    >
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog podglądu eksportu */}
            <Dialog 
                open={exportPreviewOpen} 
                onClose={() => setExportPreviewOpen(false)} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{
                    sx: {
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '24px', color: '#478E95' }}>
                    Podgląd wydruku rodowodu
                </DialogTitle>
                <DialogContent sx={{ padding: 0, overflow: 'auto', backgroundColor: '#f5f5f5' }}>
                    {pedigreeData && (
                        <Box sx={{ 
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            padding: 2,
                            minHeight: '500px'
                        }}>
                            <Box sx={{ 
                                width: '210mm',
                                minHeight: 'auto',
                                backgroundColor: '#ffffff',
                                display: 'flex',
                                flexDirection: 'column',
                                fontFamily: "'Poppins', sans-serif",
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                transform: 'scale(0.5)',
                                transformOrigin: 'top center',
                                marginBottom: '-150mm'
                            }}>
                                {/* Nagłówek */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start',
                                    padding: '15px 20px',
                                    borderBottom: '1px solid #333'
                                }}>
                                    <Box>
                                        <Typography sx={{ 
                                            fontSize: '28px', 
                                            fontWeight: 700, 
                                            color: '#333',
                                            marginBottom: '8px'
                                        }}>
                                            {breederData.name || 'Nazwa hodowli'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                                            {profileData.firstName} {profileData.lastName}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#333', marginBottom: '4px' }}>
                                            {profileData.city}
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', color: '#333' }}>
                                            {profileData.phone}
                                        </Typography>
                                    </Box>
                                    {breederData.logo && (
                                        <Box sx={{ maxWidth: '150px', maxHeight: '80px' }}>
                                            <Box 
                                                component="img"
                                                src={breederData.logo}
                                                alt="Logo hodowli"
                                                sx={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                
                                {/* Główna sekcja - tylko drzewo genealogiczne */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexGrow: 0,
                                    flexShrink: 0,
                                    padding: '15px 10px',
                                    justifyContent: 'center',
                                    alignItems: 'flex-start'
                                }}>
                                    {/* Drzewo genealogiczne - CSS Grid */}
                                    <Box key={`print-preview-grid-${columns}`} sx={{ 
                                        display: 'grid',
                                        gridTemplateColumns: (() => {
                                            const depth = columns;
                                            const gridCols = depth === 5 ? 4 : depth;
                                            const a4Width = 794;
                                            
                                            // Dla wybranej opcji "3 generacje" (columns === 3) wszystkie karty mają być tak samo szerokie i równe odstępy
                                            let gap, mainColWidth, otherColWidth;
                                            
                                            if (columns === 3) {
                                                gap = 8; // Równe odstępy
                                                const totalGapWidth = (gridCols - 1) * gap;
                                                const availableWidth = a4Width - totalGapWidth - 20;
                                                // Wszystkie kolumny równej szerokości
                                                const colWidth = Math.floor(availableWidth / gridCols);
                                                mainColWidth = colWidth;
                                                otherColWidth = colWidth;
                                            } else {
                                                gap = 6;
                                                const totalGapWidth = (gridCols - 1) * gap;
                                                const availableWidth = a4Width - totalGapWidth - 20;
                                                mainColWidth = 200;
                                                otherColWidth = gridCols > 1 ? Math.floor((availableWidth - mainColWidth) / (gridCols - 1)) : 0;
                                            }
                                            
                                            return `${mainColWidth}px repeat(${gridCols - 1}, ${otherColWidth}px)`;
                                        })(),
                                        gridTemplateRows: (() => {
                                            const depth = columns;
                                            const gridRows = Math.pow(2, depth);
                                            const availableHeight = getPedigreeGridContentHeightPx(depth);
                                            const rowGap = columns === 3 ? 8 : 6;
                                            const rowHeight = (availableHeight - (gridRows - 1) * rowGap) / gridRows;
                                            return `repeat(${gridRows}, ${rowHeight}px)`;
                                        })(),
                                        gap: (() => {
                                            return columns === 3 ? '8px' : '6px';
                                        })(),
                                        width: '794px',
                                        height: `${getPedigreeGridContentHeightPx(columns)}px`,
                                        padding: '0',
                                        backgroundColor: '#ffffff'
                                    }}>
                                        {(() => {
                                                // CSS Grid Layout oparty o drzewo binarne
                                                const depth = columns;
                                                const a4Width = 794;
                                                // Wysokość dostępna na A4 po odjęciu nagłówka (~100px), stopki (40px) i paddingów (30px)
                                                // Dla większej liczby kolumn (większej głębokości) zmniejszamy dostępną wysokość
                                                const availableHeight = getPedigreeGridContentHeightPx(depth);
                                                const gridRows = Math.pow(2, depth);
                                                const gridCols = depth === 5 ? 4 : depth;
                                                const previewGap = columns === 3 ? 8 : 6;
                                                const rowHeight = (availableHeight - (gridRows - 1) * previewGap) / gridRows;
                                                
                                                // Dla wybranej opcji "3 generacje" (columns === 3) wszystkie karty mają być tak samo szerokie i równe odstępy
                                                let gap, mainColWidth, otherColWidth, previewBaseWidth, previewParentWidth;
                                                
                                                if (columns === 3) {
                                                    // Dla 3 generacji: wszystkie kolumny równej szerokości, równe odstępy
                                                    gap = 8; // Równe odstępy
                                                    const totalGapWidth = (gridCols - 1) * gap;
                                                    const availableWidth = a4Width - totalGapWidth - 20;
                                                    // Wszystkie kolumny równej szerokości
                                                    const colWidth = Math.floor(availableWidth / gridCols);
                                                    mainColWidth = colWidth;
                                                    otherColWidth = colWidth;
                                                    previewBaseWidth = Math.min(180, mainColWidth - 20);
                                                    previewParentWidth = Math.min(160, otherColWidth - 20); // Wszystkie karty tak samo szerokie
                                                } else {
                                                    gap = 6;
                                                    const totalGapWidth = (gridCols - 1) * gap;
                                                    const availableWidth = a4Width - totalGapWidth - 20;
                                                    mainColWidth = 200;
                                                    otherColWidth = gridCols > 1 ? Math.floor((availableWidth - mainColWidth) / (gridCols - 1)) : 0;
                                                    previewBaseWidth = Math.min(180, mainColWidth - 40);
                                                    previewParentWidth = Math.min(130, otherColWidth - 10);
                                                }
                                                
                                                // Szerokość etykiety płci
                                                const previewGenderLabelWidth = 38;
                                                
                                                // Oblicz pozycje dla kolumny 0
                                                // Dla depth=5: ojciec nad, główny, matka pod (wszystko w kolumnie 0)
                                                // Dla depth<=4: tylko główny w kolumnie 0, rodzice w kolumnie 1
                                                let fatherStartRow, fatherEndRow, mainStartRow, mainEndRow, motherStartRow, motherEndRow;
                                                let previewParentHeight, previewBaseHeight, previewMotherHeight;
                                                
                                                if (depth === 5) {
                                                    // Dla 5: ojciec, główny, matka w kolumnie 0
                                                    const fatherRows = Math.floor(gridRows / 4);
                                                    const mainRows = Math.floor(gridRows / 2);
                                                    const motherRows = gridRows - fatherRows - mainRows;
                                                    
                                                    fatherStartRow = 1;
                                                    fatherEndRow = fatherStartRow + fatherRows;
                                                    mainStartRow = fatherEndRow;
                                                    mainEndRow = mainStartRow + mainRows;
                                                    motherStartRow = mainEndRow;
                                                    motherEndRow = motherStartRow + motherRows;
                                                    
                                                    previewParentHeight = fatherRows * rowHeight - (fatherRows > 1 ? gap : 0);
                                                    previewBaseHeight = mainRows * rowHeight - (mainRows > 1 ? gap : 0);
                                                    previewMotherHeight = motherRows * rowHeight - (motherRows > 1 ? gap : 0);
                                                } else {
                                                    // Dla depth<=4: tylko główny w kolumnie 0
                                                    const mainRows = gridRows;
                                                    mainStartRow = 1;
                                                    mainEndRow = mainStartRow + mainRows;
                                                    previewBaseHeight = mainRows * rowHeight - (mainRows > 1 ? gap : 0);
                                                    
                                                    // Ojciec i matka będą w kolumnie 1 (rodzice)
                                                    fatherStartRow = null;
                                                    fatherEndRow = null;
                                                    motherStartRow = null;
                                                    motherEndRow = null;
                                                    previewParentHeight = 0;
                                                    previewMotherHeight = 0;
                                                }
                                                
                                                const previewGetPathForPosition = (level, position) => {
                                                    if (level === 0) return null;
                                                    const path = [];
                                                    let currentPos = position;
                                                    for (let l = 1; l <= level; l++) {
                                                        const levelSize = Math.pow(2, l);
                                                        const halfSize = levelSize / 2;
                                                        const isRight = currentPos >= halfSize;
                                                        path.push(isRight ? 'mother' : 'father');
                                                        if (l < level) {
                                                            currentPos = currentPos % halfSize;
                                                        }
                                                    }
                                                    return path;
                                                };
                                                
                                                return (
                                                    <>
                                                        {/* Kolumna 0 */}
                                                        {depth === 5 ? (
                                                            // Dla depth=5: ojciec, główny, matka w kolumnie 0
                                                            <>
                                                                {/* Ojciec */}
                                                                {pedigreeData?.father ? (
                                                                    <Box sx={{ 
                                                                        gridRow: `${fatherStartRow} / ${fatherEndRow}`,
                                                                        gridColumn: '1',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}>
                                                                        {renderPigeonCard(pedigreeData.father, previewParentHeight, previewParentWidth, false, ['father'], true, 1, 0)}
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ 
                                                                        gridRow: `${fatherStartRow} / ${fatherEndRow}`,
                                                                        gridColumn: '1',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}>
                                                                        <Box sx={{ 
                                                                            border: '1px solid #ccc',
                                                                            borderRadius: 1,
                                                                            padding: 1,
                                                                            height: `${previewParentHeight}px`,
                                                                            width: `${previewParentWidth}px`,
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            backgroundColor: '#f5f5f5',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            <Typography sx={{ color: '#999', fontFamily: "'Poppins', sans-serif", fontSize: '8px' }}>
                                                                                brak
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                                
                                                                {/* Główny gołąb */}
                                                                {pedigreeData && (
                                                                    <Box sx={{ 
                                                                        gridRow: `${mainStartRow} / ${mainEndRow}`,
                                                                        gridColumn: '1',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}>
                                                                        {renderPigeonCard(pedigreeData, previewBaseHeight, previewBaseWidth, true, null, true, 0, 0)}
                                                                    </Box>
                                                                )}
                                                                
                                                                {/* Matka */}
                                                                {pedigreeData?.mother ? (
                                                                    <Box sx={{ 
                                                                        gridRow: `${motherStartRow} / ${motherEndRow}`,
                                                                        gridColumn: '1',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}>
                                                                        {renderPigeonCard(pedigreeData.mother, previewMotherHeight, previewParentWidth, false, ['mother'], true, 1, 1)}
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ 
                                                                        gridRow: `${motherStartRow} / ${motherEndRow}`,
                                                                        gridColumn: '1',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}>
                                                                        <Box sx={{ 
                                                                            border: '1px solid #ccc',
                                                                            borderRadius: 1,
                                                                            padding: 1,
                                                                            height: `${previewMotherHeight}px`,
                                                                            width: `${previewParentWidth}px`,
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            backgroundColor: '#f5f5f5',
                                                                            justifyContent: 'center',
                                                                            alignItems: 'center'
                                                                        }}>
                                                                            <Typography sx={{ color: '#999', fontFamily: "'Poppins', sans-serif", fontSize: '8px' }}>
                                                                                brak
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            </>
                                                        ) : (
                                                            // Dla depth<=4: tylko główny w kolumnie 0
                                                            <>
                                                                {/* Główny gołąb */}
                                                                {pedigreeData && (
                                                                    <Box sx={{ 
                                                                        gridRow: `${mainStartRow} / ${mainEndRow}`,
                                                                        gridColumn: '1',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-start'
                                                                    }}>
                                                                        {renderPigeonCard(pedigreeData, previewBaseHeight, previewBaseWidth, true, null, true, 0, 0)}
                                                                    </Box>
                                                                )}
                                                            </>
                                                        )}
                                                        
                                                        {/* Pozostałe kolumny */}
                                                        {Array.from({ length: gridCols - 1 }, (_, colIndex) => {
                                                            // Dla depth=5: kolumna 1 = poziom 2 (dziadkowie), kolumna 2 = poziom 3, kolumna 3 = poziom 4
                                                            // Dla depth<=4: kolumna 1 = poziom 1 (rodzice), kolumna 2 = poziom 2 (dziadkowie), itd.
                                                            const level = depth === 5 ? colIndex + 2 : colIndex + 1;
                                                            const maxPigeonsAtLevel = Math.pow(2, level);
                                                            const col = colIndex + 2; // Kolumna 2, 3, 4... (kolumna 1 to główna)
                                                            
                                                            // Każdy gołąb zajmuje dokładnie równą część z 2^depth wierszy
                                                            const exactRowsPerPigeon = gridRows / maxPigeonsAtLevel;
                                                            
                                                            return (
                                                                <React.Fragment key={colIndex}>
                                                                    {Array.from({ length: maxPigeonsAtLevel }, (_, index) => {
                                                                        const path = previewGetPathForPosition(level, index);
                                                                        const pigeon = getPigeonByPath(pedigreeData, path);
                                                                        // Dokładne obliczenie pozycji - każdy gołąb zajmuje dokładnie exactRowsPerPigeon wierszy
                                                                        const rowsPerNode = gridRows / Math.pow(2, level);
                                                                        const startRow = index * rowsPerNode + 1;
                                                                        const rowSpan = rowsPerNode;
                                                                        const cardHeight = rowSpan * rowHeight - (rowSpan - 1) * gap;
                                                                        
                                                                        if (!pigeon) {
                                                                            return (
                                                                                <Box 
                                                                                    key={index}
                                                                                    sx={{ 
                                                                                        gridRow: `${startRow} / ${startRow + rowSpan}`,
                                                                                        gridColumn: col,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'flex-start'
                                                                                    }}
                                                                                >
                                                                                    <Box sx={{
                                                                                        border: '1px solid #ccc',
                                                                                        borderRadius: 1,
                                                                                        padding: level <= 2 ? 1 : 0.5,
                                                                                        height: `${cardHeight}px`,
                                                                                        width: `${previewParentWidth}px`,
                                                                                        display: 'flex',
                                                                                        flexDirection: 'column',
                                                                                        backgroundColor: '#f5f5f5',
                                                                                        justifyContent: 'center',
                                                                                        alignItems: 'center'
                                                                                    }}>
                                                                                        <Typography sx={{ 
                                                                                            color: '#999', 
                                                                                            fontFamily: "'Poppins', sans-serif", 
                                                                                            fontSize: level <= 2 ? '8px' : '6px' 
                                                                                        }}>
                                                                                            brak
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        }
                                                                        
                                                                        return (
                                                                            <Box 
                                                                                key={pigeon.id || index}
                                                                                sx={{ 
                                                                                    gridRow: `${startRow} / ${startRow + rowSpan}`,
                                                                                    gridColumn: col,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'flex-start'
                                                                                }}
                                                                            >
                                                                                {renderPigeonCard(pigeon, cardHeight, previewParentWidth, false, path, true, level, index)}
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </>
                                                );
                                            })()}
                                    </Box>
                                </Box>
                                
                                {/* Stopka */}
                                <Box sx={{ 
                                    backgroundColor: '#57C09A',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '0 20px'
                                }}>
                                    <Typography sx={{ 
                                        color: 'white', 
                                        fontSize: '14px',
                                        fontWeight: 600
                                    }}>
                                        Lotnik 360
                                    </Typography>
                                    <Box component="img" src={Logo} alt="Lotnik 360" sx={{ height: 22, width: 'auto', objectFit: 'contain' }} />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: '20px' }}>
                    <Button 
                        onClick={() => setExportPreviewOpen(false)}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            color: '#707070'
                        }}
                    >
                        Anuluj
                    </Button>
                    <Button 
                        onClick={handleExportPedigree}
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        sx={{
                            fontFamily: "'Poppins', sans-serif",
                            textTransform: 'none',
                            backgroundColor: '#57C09A',
                            '&:hover': {
                                backgroundColor: '#4fcda2'
                            }
                        }}
                    >
                        Pobierz PDF
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Pedigree;
